import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import clsx from "clsx";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  removeElements,
  Controls,
  ControlButton,
  Background,
  isNode,
  useZoomPanHelper,
  useStoreActions,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import "react-flow-renderer/dist/theme-default.css";
import "./canvasAnim.css";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import BackIcon from "@material-ui/icons/ArrowBack";
import GoToNodeIcon from "@material-ui/icons/WebAssetOutlined";
import PublishIcon from "@material-ui/icons/CloudUpload";
import WarnIcon from "@material-ui/icons/ReportProblemOutlined";
import OrganizeIcon from "@material-ui/icons/AccountTreeOutlined";
import SvgIcon from "@material-ui/core/SvgIcon";
import EditModeIcon from "@material-ui/icons/EditOutlined";
import ChevronRightIcon from "@material-ui/icons/ChevronRightRounded";
import UndoIcon from "@material-ui/icons/UndoRounded";
import RedoIcon from "@material-ui/icons/RedoRounded";
import ViewModeIcon from "@material-ui/icons/VisibilityOutlined";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircleOutline";
import HistoryIcon from "@material-ui/icons/RestoreOutlined";
import GuideIcon from "@material-ui/icons/FlagOutlined";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import RenameIcon from "@material-ui/icons/CreateOutlined";
import SwapIcon from "@material-ui/icons/CachedOutlined";

import CopilotPanel, { collectorFlowProfile } from "./CopilotPanel";
import { handoffProfile } from "./copilotAgents";
import { makeCollectorRunTool } from "./collectorCopilot";
import LiveEventsPanel from "./LiveEventsPanel";
import WhatsappTemplatePending from "./WhatsappTemplatePending";
import FlowNotesModal from "./FlowNotesModal";
import FlowSettingsModal from "./FlowSettingsModal";
import SettingsIcon from "@material-ui/icons/SettingsOutlined";
import ExecutionsPill from "./ExecutionsPill";
import { format } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";
import { useProject } from "../../context/Project/ProjectContext";
import AddIcon from "@material-ui/icons/Add";
import { i18n } from "../../translate/i18n";
import { VERBS, stepNamesFor } from "./verbs";
import { connectToSocket } from "../../services/socket";
import { flowErrors } from "./flowValidation";
import { triggerDirection, triggerProvidesCall, TRIGGERS } from "./triggers";
import { nodeTypes, nodeAccent } from "./CustomNodes";
import { compile } from "./compile";
import ConfigDrawer from "./ConfigDrawer";
import TriggerModal from "./TriggerModal";
import AddNodeModal from "./AddNodeModal";
import ToolTesterModal from "./ToolTesterModal";
import SetupTour from "./SetupTour";
import { detectPending } from "./setupGuide";
import { BuilderActionsContext } from "./builderActions";

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: "#fff",
    color: theme.palette.primary.main,
    boxShadow: "none",
    borderBottom: `1px solid ${theme.app.border}`,
  },
  toolbar: {
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      rowGap: 6,
      paddingTop: 6,
      paddingBottom: 6,
    },
  },
  name: {
    marginLeft: theme.spacing(1),
    width: 200,
    "& input": { fontSize: 13.5, fontWeight: 600 },
    [theme.breakpoints.down("sm")]: { width: 130 },
  },
  trigger: { marginLeft: theme.spacing(2), width: 130 },
  grow: { flexGrow: 1, [theme.breakpoints.down("sm")]: { flexBasis: "100%", height: 0 } },
  wrap: {
    display: "flex",
    height: "calc(100vh - 64px - 64px)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", height: "auto" },
  },
  // trilha fina quando o copiloto está recolhido (só abaixo de 1280)
  copilotRail: {
    width: 44,
    flexShrink: 0,
    borderRight: `1px solid ${theme.app.border}`,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 10,
    gap: 8,
  },
  copilotRailBtn: { color: theme.app.dark },
  copilotRailLabel: { writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 11, color: theme.app.textTertiary, letterSpacing: ".04em" },
  // chegada suave quando veio da home (?build=) — evita flash de troca de página
  seedFade: { animation: "$seedFadeIn .45s ease both" },
  "@keyframes seedFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
  canvas: {
    flexGrow: 1,
    height: "100%",
    minWidth: 0,
    position: "relative",
    background: theme.app.chatBg,
    // sistema de nós (refactor): edges finas e neutras (combinam com os cards)
    "& .react-flow__edge-path": { stroke: "#CDD3D9", strokeWidth: 2 },
    "& .react-flow__edge.selected .react-flow__edge-path, & .react-flow__edge:hover .react-flow__edge-path": { stroke: theme.app.dark },
    "& .react-flow__arrowhead": { fill: "#CDD3D9" },
    [theme.breakpoints.down("sm")]: { height: "58vh", minHeight: 320 },
  },
  viewBadge: {
    position: "absolute",
    // abaixo do pill de execuções (top 16 + 44 de altura) p/ não sobrepor
    top: 64,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 16,
    background: "rgba(44,53,61,0.92)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(44,53,61,0.25)",
  },
  viewBadgeEdit: {
    marginLeft: 4,
    paddingLeft: 10,
    borderLeft: "1px solid rgba(255,255,255,0.25)",
    color: theme.app.lime,
    cursor: "pointer",
    fontWeight: 700,
    "&:hover": { textDecoration: "underline" },
  },
  multiPanel: {
    width: 360,
    flexShrink: 0,
    borderLeft: `1px solid ${theme.app.border}`,
    background: "#fff",
    padding: theme.spacing(2),
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  multiCount: { fontWeight: 700, fontSize: 16, marginBottom: theme.spacing(0.5) },
  multiHint: { color: theme.app.textTertiary, fontSize: 12, marginBottom: theme.spacing(2) },
  multiActions: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  pre: {
    background: "#0f1115",
    color: "#d6f76b",
    padding: 16,
    borderRadius: 8,
    fontSize: 12,
    maxHeight: "55vh",
    overflow: "auto",
    whiteSpace: "pre",
  },
  warn: { color: "#B26A00", fontSize: 13, marginTop: 8 },
  status: { marginLeft: theme.spacing(1.5), height: 24, fontWeight: 700 },
  statusDraft: { backgroundColor: "#FFF4E0", color: "#B26A00" },
  statusPub: { backgroundColor: "#E6F4EA", color: "#1E7E34" },
  chipPub: { backgroundColor: "#E6F4EA", color: "#1E7E34", fontWeight: 600, height: 22 },
  chipArch: { backgroundColor: theme.app.chip, color: theme.app.textSecondary, fontWeight: 600, height: 22 },
  sid: { fontFamily: "monospace", fontSize: 11, color: theme.app.textTertiary },
  // ---- cabeçalho (redesign) ----
  backBox: { width: 32, height: 32, borderRadius: 8, border: `1px solid ${theme.app.border}`, color: theme.app.textSecondary },
  idy: { display: "flex", flexDirection: "column", marginLeft: 10, minWidth: 0 },
  // botão de notas: colado ao título (a nota é sobre o FLUXO, não é uma ação de
  // edição). O estado "tem conteúdo" pinta o botão INTEIRO — um pontinho ao lado
  // do nome desaparece na tela.
  notesBtn: {
    width: 40, height: 40, marginLeft: 8, flexShrink: 0,
    borderRadius: 10, border: `1px solid ${theme.app.border}`,
    color: theme.app.textSecondary, background: "#fff",
    "&:hover": { background: theme.app.hover },
  },
  notesBtnOn: {
    background: "#F2F6E5", borderColor: "#DCE9BF", color: "#4d7a1e",
    "&:hover": { background: "#EAF1D7" },
  },
  nameField: {
    width: 240,
    "& input": { fontSize: 15, fontWeight: 600, padding: "1px 0", color: theme.app.ink },
    "& .MuiInput-underline:before": { borderBottom: "none" },
    "& .MuiInput-underline:after": { borderBottom: `2px solid ${theme.app.dark}` },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: `1px solid ${theme.app.border}` },
  },
  statusLine: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.app.textSecondary, marginTop: -1 },
  statusDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  dotOk: { background: theme.app.ok },
  dotDraft: { background: "#C77A12" },
  statusVer: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary },
  seg: { display: "flex", background: "#F0F2F4", borderRadius: 9, padding: 3, marginRight: 8 },
  segBtn: {
    border: "none",
    background: "transparent",
    font: "inherit",
    fontSize: 13,
    fontWeight: 500,
    color: theme.app.textSecondary,
    padding: "6px 13px",
    borderRadius: 7,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    "& svg": { fontSize: 16 },
  },
  segBtnOn: { background: "#fff", color: theme.app.ink, boxShadow: "0 1px 3px rgba(0,0,0,.08)", fontWeight: 600 },
  // indicador de autosave ("Salvo · há Xs" / "Salvando…" / erro)
  saveInd: { display: "inline-flex", alignItems: "center", marginRight: 10, minWidth: 84, justifyContent: "flex-end" },
  saveMuted: { fontSize: 12, color: theme.app.textTertiary, whiteSpace: "nowrap" },
  saveErr: { fontSize: 12, color: theme.app.danger, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } },
  histBtn: { color: theme.app.textSecondary, padding: 6, "&:hover": { color: theme.app.ink, background: theme.app.hover }, "&.Mui-disabled": { color: theme.app.border } },
  diffList: { margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: theme.app.ink, "& li": { marginBottom: 2 } },
  iconBox: { width: 34, height: 34, borderRadius: 8, border: `1px solid ${theme.app.border}`, color: theme.app.textSecondary, marginRight: 8 },
  // menu de contexto do canvas — mesmo padrão do menu do projeto (header)
  ctxPaper: { minWidth: 200, marginTop: 4, padding: 6, background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 12, boxShadow: "0 14px 38px rgba(27,34,42,.16)", color: theme.app.ink },
  ctxItem: { fontSize: 12.5, fontWeight: 500, color: theme.app.ink, borderRadius: 8, padding: "8px 10px", gap: 11, marginBottom: 1, "& svg": { fontSize: 18, color: theme.app.textTertiary }, "&:hover": { background: theme.app.hover } },
  ctxDanger: { color: theme.app.danger || "#C0453B", "& svg": { color: theme.app.danger || "#C0453B" } },
  tplPendingChip: { display: "inline-flex", alignItems: "center", gap: 6, marginRight: 8, padding: "5px 11px", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#9A6410", background: "#FBF1DE", border: "1px solid #EBD9B4", cursor: "pointer", whiteSpace: "nowrap", "&:hover": { background: "#F7E9CE" } },
  // chip "Rascunho": há alterações salvas ainda não publicadas (ao lado de Publicada vN).
  // Discreto de propósito — é um aviso leve, não um alerta.
  draftChip: { display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 10, padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 500, color: theme.app.warn, background: "#FEFCE8", border: "1px solid #F1E4B8", cursor: "pointer", whiteSpace: "nowrap", font: "inherit", "&:hover": { background: "#FBF6DC" } },
}));

const initialElements = () => [
  { id: "start", type: "triggerNode", data: { verbKey: "__trigger__" }, position: { x: 260, y: 20 } },
];

const isTriggerNode = el =>
  el.data && (el.data.verbKey === "__trigger__" || el.data.verbKey === "__start__");

// diff estrutural entre o flow PUBLICADO e o rascunho atual (ids dos nós são estáveis
// entre versões — reseedIds não re-numera existentes). Usado no "Publicar".
const nodeLabelOf = n => { const d = (n && n.data) || {}; const vd = VERBS[d.verbKey] || {}; return vd.title || d.verbKey || "nó"; };
const diffFlow = (pubFlow, curEls) => {
  const isReal = el => isNode(el) && !isTriggerNode(el);
  const pub = (pubFlow || []).filter(isReal);
  const cur = (curEls || []).filter(isReal);
  const pubById = new Map(pub.map(n => [n.id, n]));
  const curById = new Map(cur.map(n => [n.id, n]));
  const added = cur.filter(n => !pubById.has(n.id)).map(nodeLabelOf);
  const removed = pub.filter(n => !curById.has(n.id)).map(nodeLabelOf);
  const changed = cur
    .filter(n => pubById.has(n.id) && JSON.stringify(pubById.get(n.id).data) !== JSON.stringify(n.data))
    .map(nodeLabelOf);
  const pubTrig = (pubFlow || []).find(isTriggerNode);
  const curTrig = (curEls || []).find(isTriggerNode);
  const triggerChanged = JSON.stringify((pubTrig && pubTrig.data) || null) !== JSON.stringify((curTrig && curTrig.data) || null);
  const eKey = e => `${e.source}->${e.target}:${e.sourceHandle || ""}`;
  const pubE = new Set((pubFlow || []).filter(el => !isNode(el)).map(eKey));
  const curEarr = (curEls || []).filter(el => !isNode(el)).map(eKey);
  const curESet = new Set(curEarr);
  const edgesAdded = curEarr.filter(k => !pubE.has(k)).length;
  const edgesRemoved = [...pubE].filter(k => !curESet.has(k)).length;
  const total = added.length + removed.length + changed.length + edgesAdded + edgesRemoved + (triggerChanged ? 1 : 0);
  return { added, removed, changed, triggerChanged, edgesAdded, edgesRemoved, total };
};

// auto-layout em ÁRVORE: organiza os nós de cima p/ baixo (por profundidade), com
// os filhos espalhados horizontalmente e o pai centralizado sobre eles — sem
// sobreposição. Ignora o frame de loop (derivado). Usado pelo botão "Organizar"
// e após o copiloto montar (que empilha tudo no mesmo lugar).
const autoLayoutElements = els => {
  const nodes = els.filter(el => isNode(el) && el.type !== "loopFrame");
  const edges = els.filter(el => el && el.source && el.target);
  const ids = new Set(nodes.map(n => n.id));
  const children = {};
  const indeg = {};
  nodes.forEach(n => {
    children[n.id] = [];
    indeg[n.id] = 0;
  });
  edges
    .slice()
    .sort((a, b) => String(a.sourceHandle || "").localeCompare(String(b.sourceHandle || "")))
    .forEach(e => {
      if (ids.has(e.source) && ids.has(e.target)) {
        children[e.source].push(e.target);
        indeg[e.target] += 1;
      }
    });
  const VGAP = 175;
  const HGAP = 250;
  const pos = {};
  const seen = new Set();
  let nextX = 0;
  const place = (id, depth) => {
    if (seen.has(id)) return;
    seen.add(id);
    const kids = children[id].filter(k => !seen.has(k));
    if (!kids.length) {
      pos[id] = { x: nextX * HGAP, y: depth * VGAP };
      nextX += 1;
      return;
    }
    const xs = [];
    kids.forEach(k => {
      place(k, depth + 1);
      if (pos[k]) xs.push(pos[k].x);
    });
    pos[id] = { x: xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : nextX * HGAP, y: depth * VGAP };
  };
  // raízes: o gatilho primeiro, depois quaisquer nós sem aresta de entrada
  const roots = nodes
    .filter(isTriggerNode)
    .map(n => n.id)
    .concat(nodes.filter(n => indeg[n.id] === 0 && !isTriggerNode(n)).map(n => n.id));
  roots.forEach(r => place(r, 0));
  nodes.forEach(n => {
    if (!seen.has(n.id)) place(n.id, 0); // desconectados
  });
  const minX = Math.min(0, ...Object.values(pos).map(p => p.x));
  return els.map(el =>
    pos[el.id] ? { ...el, position: { x: Math.round(pos[el.id].x - minX + 80), y: pos[el.id].y + 20 } } : el
  );
};

// migra nós antigos de "Enviar WhatsApp" (Gupshup) do JSON cru (body string) p/
// o formulário amigável novo (destination + text), pra não perderem a config.
const migrateGupshupText = el => {
  if (!el || !el.data || el.data.verbKey !== "conn.gupshup.enviar_whatsapp") return el;
  const d = el.data;
  if (d.destination != null || d.text != null || d.body == null) return el; // já novo
  try {
    const b = typeof d.body === "string" ? JSON.parse(d.body) : d.body;
    let text = "";
    if (b && b.message) {
      const m = typeof b.message === "string" ? JSON.parse(b.message) : b.message;
      text = (m && m.text) || "";
    }
    return { ...el, data: { ...d, destination: (b && b.destination) || "", text } };
  } catch (e) {
    return el;
  }
};

// indentação visual dos passos DENTRO de um bloco de repetição (Modelo B):
// quanto os nós internos são deslocados à direita (só no display) p/ parecerem
// aninhados. A posição salva NÃO muda (traduzida de volta no drag).
const LOOP_INDENT = 26;
// largura do container de repetição (= indent + card interno 264 + margem); o
// cabeçalho/rodapé usam a MESMA largura p/ ficarem flush com o frame.
const LOOP_FRAME_W = 344;
// membros do BLOCO de um loop (a partir do nó cabeçalho `loopId`): os passos
// internos + os loops aninhados + o loop_end que casa. NÃO inclui o cabeçalho.
// Usado para mover o bloco inteiro junto ao arrastar o cabeçalho.
const loopBlockIds = (els, loopId) => {
  const ns = (els || [])
    .filter(el => el && el.data && !el.source && el.position && typeof el.position.y === "number")
    .sort((a, b) => a.position.y - b.position.y);
  const i = ns.findIndex(n => n.id === loopId);
  if (i < 0 || ns[i].data.verbKey !== "loop") return [];
  const ids = [];
  let depth = 1;
  for (let j = i + 1; j < ns.length; j += 1) {
    const vk = ns[j].data.verbKey;
    ids.push(ns[j].id);
    if (vk === "loop") depth += 1;
    else if (vk === "loop_end") {
      depth -= 1;
      if (depth === 0) break;
    }
  }
  return ids;
};

// acha o cabeçalho (loop) que casa com um dado loop_end (o par do bloco).
const findLoopHeadForEnd = (els, endId) => {
  const loops = (els || []).filter(el => el && el.data && el.data.verbKey === "loop");
  for (const l of loops) {
    const block = loopBlockIds(els, l.id);
    if (block.length && block[block.length - 1] === endId) return l.id;
  }
  return null;
};

// ids dos nós que estão ENTRE um loop e seu loop_end (por ordem vertical, ciente
// de aninhamento). Os próprios marcadores não contam.
const loopInnerIds = els => {
  const set = new Set();
  const ns = (els || [])
    .filter(el => el && el.data && !el.source && el.position && typeof el.position.y === "number")
    .sort((a, b) => a.position.y - b.position.y);
  let depth = 0;
  ns.forEach(n => {
    const vk = n.data.verbKey;
    if (vk === "loop") depth += 1;
    else if (vk === "loop_end") depth = Math.max(0, depth - 1);
    else if (depth > 0) set.add(n.id);
  });
  return set;
};

const edgeOpts = { type: "smoothstep", arrowHeadType: "arrowclosed" };
// zoom máximo do enquadramento inicial/reorganização (fluxos pequenos não vão ao
// máximo). Pós-publicar usa um teto menor (ver PUBLISH_ZOOM) pra caber mais nós.
// teto de zoom do fitView (load/organizar/centralizar). É o MESMO teto usado no
// botão de centralizar (onFitView). 0.64 -> 0.77 = abre ~20% mais zoom.
const INITIAL_ZOOM = 0.77;
const PUBLISH_ZOOM = 0.85;

// projeto protegido (hardcoded): apps do "Sistema Numera" abrem read-only — sem
// modo de edição. Mesma checagem/mensagem da lista de automações.
const PROTECTED_PROJECT = "Sistema Numera";

// ícone "NotebookPen" (lucide) — MUI v4 não tem, então inline via SvgIcon (traço, não preenchido)
const NotebookPenIcon = props => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", ...(props.style || {}) }}
  >
    <path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4" />
    <path d="M2 6h4" />
    <path d="M2 10h4" />
    <path d="M2 14h4" />
    <path d="M2 18h4" />
    <path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" />
  </SvgIcon>
);

// Infere os CAMPOS que um nó de código expõe, lendo as chaves de nível 0 do
// último `return { ... }`. Assim o picker mostra steps.<código>.<campo> mesmo
// ANTES de rodar (o nó de código é de saída dinâmica). Trata chaves nuas e entre
// aspas; ignora o que está dentro de objetos/arrays/strings aninhados.
const inferReturnKeys = code => {
  if (typeof code !== "string") return [];
  const idx = code.lastIndexOf("return");
  if (idx < 0) return [];
  const start = code.indexOf("{", idx);
  if (start < 0) return [];
  let depth = 0;
  let str = null;
  let end = -1;
  for (let i = start; i < code.length; i += 1) {
    const c = code[i];
    if (str) {
      if (c === "\\") i += 1;
      else if (c === str) str = null;
      continue;
    }
    if (c === '"' || c === "'" || c === "`") str = c;
    else if (c === "{") depth += 1;
    else if (c === "}") {
      depth -= 1;
      if (depth === 0) { end = i; break; }
    }
  }
  if (end < 0) return [];
  const body = code.slice(start + 1, end);
  const keys = [];
  const isIdent = t => /^[A-Za-z_$][\w$]*$/.test(t);
  let d = 0;
  let s = null;
  let tok = "";
  let expectingKey = true; // true = esperando uma chave; false = dentro do valor
  const flushShorthand = () => { if (expectingKey && isIdent(tok)) keys.push(tok); tok = ""; };
  for (let i = 0; i < body.length; i += 1) {
    const c = body[i];
    if (s) {
      if (c === "\\") { i += 1; continue; }
      if (c === s) s = null;
      continue;
    }
    if (c === '"' || c === "'" || c === "`") {
      // chave entre aspas (só conta se estamos esperando uma chave, no nível 0)
      let j = i + 1;
      let val = "";
      while (j < body.length && body[j] !== c) {
        if (body[j] === "\\") j += 1;
        else val += body[j];
        j += 1;
      }
      if (d === 0 && expectingKey) tok = val;
      i = j;
      continue;
    }
    if (c === "{" || c === "[" || c === "(") { d += 1; continue; }
    if (c === "}" || c === "]" || c === ")") { d -= 1; continue; }
    if (d !== 0) continue;
    if (c === ":") { if (isIdent(tok)) keys.push(tok); tok = ""; expectingKey = false; } // chave explícita
    else if (c === ",") { flushShorthand(); expectingKey = true; } // fim do par → shorthand?
    else if (/[A-Za-z0-9_$]/.test(c)) { if (expectingKey) tok += c; }
    else if (!/\s/.test(c)) tok = ""; // qualquer outro símbolo quebra o token (espaço preserva)
  }
  flushShorthand(); // último par (shorthand) antes do fim do objeto
  return Array.from(new Set(keys));
};

// copiloto de construção: catálogo enriquecido + validate_flow + contexto da conta
const COPILOT_ENABLED = true;

// multi-select key differs by OS, and must NOT equal the box-select key
// (selectionKeyCode renders an overlay that would swallow the click). So:
//   Mac   -> Cmd (Meta) adds to selection; Shift+drag draws the box
//   other -> Shift adds to selection; Ctrl+drag draws the box
const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
const MULTI_SELECT_KEY = IS_MAC ? "Meta" : "Shift";
const BOX_SELECT_KEY = IS_MAC ? "Shift" : "Control";

// id do nó provisório "Criando automação…" (só na tela; ver displayElements)
const PLACEHOLDER_ID = "__copilot_placeholder__";

const BuilderInner = () => {
  const { confirm, prompt } = useConfirm();
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const { activeProjectId, projects: projectList } = useProject();
  const { applicationId } = useParams();
  const location = useLocation();
  const isNew = applicationId === "new";

  // Se o usuário trocar (ou criar) o projeto ativo no header enquanto edita um
  // fluxo, ele estaria editando um fluxo de um projeto que "mudou" atrás dele.
  // Ao detectar a troca do projeto ativo, volta para a lista (já no novo projeto).
  const projGuardRef = useRef(activeProjectId);
  useEffect(() => {
    if (projGuardRef.current == null) { projGuardRef.current = activeProjectId; return; }
    if (activeProjectId != null && activeProjectId !== projGuardRef.current) {
      projGuardRef.current = activeProjectId;
      history.push("/applications");
    }
  }, [activeProjectId, history]);
  // a new app is created from a project (?projectId=, mandatory) and optionally a
  // folder (?folderId=) — both passed by the Apps page.
  const query = new URLSearchParams(location.search);
  const queryProjectId = query.get("projectId") ? Number(query.get("projectId")) : null;
  const queryFolderId = query.get("folderId") ? Number(query.get("folderId")) : null;
  // "sabor" do canvas: tool (ferramenta) / agent — preset do gatilho p/ não pedir
  // um gatilho de chamada (esses são fluxos backend-only / agente).
  const queryKind = query.get("kind");

  const [elements, setElements] = useState(initialElements());
  const [name, setName] = useState(isNew ? "Sem Título" : "");
  // "readme" do fluxo (markdown). Salvo à parte do fluxo: documentar não é
  // alteração do que roda, então não passa pelo salvar/publicar.
  const [notes, setNotes] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const hasNotes = !!String(notes || "").trim();

  // Etapa 4 (MCP): posse (aiEditable) + ciclo (inDevelopment) do fluxo. Não afetam
  // o que roda — salvos à parte, sem marcar "alterações não publicadas".
  const [aiEditable, setAiEditable] = useState(false);
  const aiEditableMarkedRef = useRef(false); // autoria do copiloto já gravada (ver autoSaveDraft)
  const [inDevelopment, setInDevelopment] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // só a POSSE (aiEditable) é do usuário aqui; inDevelopment é gerenciado pela IA
  // (via MCP) — não é tocado por este save.
  const saveFlowSettings = async next => {
    setAiEditable(!!next.aiEditable);
    if (!applicationId) return;
    try {
      await api.put(`/applications/${applicationId}`, { aiEditable: !!next.aiEditable });
      toast.success("Configurações salvas.");
    } catch (err) {
      toastError(err);
    }
  };

  // salva SÓ as notas, sem passar pelo fluxo: documentar não muda o que roda e
  // não pode marcar "alterações não publicadas" nem exigir publicar de novo.
  const saveNotes = async texto => {
    setNotes(texto);
    if (!applicationId) return;
    try {
      await api.put(`/applications/${applicationId}`, { notes: texto });
      toast.success("Notas salvas.");
    } catch (err) {
      toastError(err);
    }
  };
  const [description, setDescription] = useState("");
  // refs sempre-atuais (o autoSaveDraft do copiloto roda num closure que pode ficar
  // stale; ler dos refs garante que o SALVO tem o estado atual — não só o gatilho)
  const nameRef = useRef(name);
  const descRef = useRef(description);
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { descRef.current = description; }, [description]);
  const [projectId, setProjectId] = useState(queryProjectId); // mandatory project
  const [folderId, setFolderId] = useState(queryFolderId); // optional folder
  const [folders, setFolders] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [nameModal, setNameModal] = useState(null); // { action: "save"|"publish" } | null
  const [suggestedName, setSuggestedName] = useState("");
  const suggestHashRef = useRef("");
  const [nameError, setNameError] = useState("");
  const [flowIssues, setFlowIssues] = useState(null); // erros de validação bloqueando publish
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]); // multi-selection (Shift)
  const [rfInstance, setRfInstance] = useState(null);
  // auto-pan da construção do copiloto: setCenter(x,y,zoom,duration) do zoomPanHelper.
  // Guardado em ref pra ser lido dentro do runCopilotTool (que tem deps []).
  const zoomPan = useZoomPanHelper();
  const zphRef = useRef(zoomPan);
  zphRef.current = zoomPan;

  // seleciona um nó NO CANVAS (borda) além de abrir o painel (setSelectedId). O
  // react-flow guarda a seleção no store próprio, então setSelectedId sozinho não
  // muda a borda — sincronizamos aqui. Usado por "Ir ao nó" do modal de problemas.
  const setRfSelected = useStoreActions(a => a.setSelectedElements);
  const goToNode = useCallback((id) => {
    setSelectedId(id);
    try { setRfSelected([{ id, type: "node" }]); } catch (e) { /* store não pronto */ }
  }, [setRfSelected]);
  const rfInstanceRef = useRef(rfInstance);
  rfInstanceRef.current = rfInstance;
  const [dirty, setDirty] = useState(true);
  const [publishedVersion, setPublishedVersion] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [webhookId, setWebhookId] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(null); // { sourceId, sourceHandle }
  const [pendingIterate, setPendingIterate] = useState(null); // { sourceId, listToken }
  const [testerOpen, setTesterOpen] = useState(false);
  const [setup, setSetup] = useState(null); // guia "Primeiros passos" (definition.setup)
  const [setupOpen, setSetupOpen] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  // id NUMÉRICO do app (a URL/applicationId pode ser o `code` opaco). Os eventos
  // são gravados/consultados por id numérico, então o painel de Live Events
  // precisa do numérico — não do code (a coluna é int; o code viraria 0).
  const [numericId, setNumericId] = useState(null);
  // meta do app lida pelo copiloto (runCopilotTool tem deps [] → usa refs p/ não
  // ficar stale): id, publicado e webhookId → montam o endpoint em read_flow.
  const appMetaRef = useRef({ id: null, published: false, webhookId: null });
  useEffect(() => {
    appMetaRef.current = { id: numericId, published: !!publishedVersion, webhookId };
  }, [numericId, publishedVersion, webhookId]);
  // Copiloto é COLUNA fixa na tela de fluxo. `copilotCollapsed` só existe p/ o
  // recolher abaixo de 1280 (única forma de esconder). Preferência persistida.
  const [copilotCollapsed, setCopilotCollapsed] = useState(() => {
    try { return localStorage.getItem("nb_copilot_collapsed") === "1"; } catch (e) { return false; }
  });
  // pulso de atenção: ao clicar em "Criar com IA" (card do canvas vazio) o copiloto
  // abre e a caixa de input dá um glow + foco, pra puxar o olhar do usuário pra lá.
  // Zera ao recolher: reabrir pelo trilho remonta o painel, mas sem pulso residual.
  const [copilotPulse, setCopilotPulse] = useState(0);
  const pulseCopilot = () => setCopilotPulse(n => n + 1);
  const setCopilotCollapsedP = v => {
    setCopilotCollapsed(v);
    if (v) setCopilotPulse(0);
    try { localStorage.setItem("nb_copilot_collapsed", v ? "1" : "0"); } catch (e) { /* ignore */ }
  };
  const [pubMenuAnchor, setPubMenuAnchor] = useState(null); // dropdown do "Publicar"
  const [ctxMenu, setCtxMenu] = useState(null); // menu de contexto do canvas: { x, y, kind, nodeId }
  const [templateModalOpen, setTemplateModalOpen] = useState(false); // modal de templates WhatsApp
  const [toolGuide, setToolGuide] = useState(null); // guia "como configurar NA FERRAMENTA" (ex.: ação de campanha no Mautic)
  const [templateFocus, setTemplateFocus] = useState(null); // nó a focar ao abrir o modal
  // handoff do chat: ?build=<spec> abre o copiloto já construindo
  const buildSeedRef = useRef(
    (() => {
      try {
        return new URLSearchParams(window.location.search).get("build") || "";
      } catch (e) {
        return "";
      }
    })()
  );
  // demo (?demo=1): copiloto NÃO pede info em tempo real, presume padrões e decide
  const demoSeedRef = useRef(
    (() => {
      try {
        return new URLSearchParams(window.location.search).get("demo") === "1";
      } catch (e) {
        return false;
      }
    })()
  );
  // projeto protegido (hardcoded): apps do "Sistema Numera" são read-only —
  // não podem ser editados nem apagados. Para mexer, o usuário move de projeto.
  const lockedProject = !!projectList.find(p => p.id === projectId && p.name === PROTECTED_PROJECT);
  // SEM modo view/edit: o canvas é SEMPRE editável (autosave no rascunho). Read-only
  // só por motivo EXTERNO — hoje só o projeto protegido; futuros (papel visualizador,
  // versão antiga aberta, execução em foco) entram aqui e o banner diz o motivo.
  const readOnly = lockedProject;
  // LAYOUT da nova automação: abre em "hero" (chat ~70% / canvas ~30%, igual à tela
  // de nova conversa) e, quando a IA coloca o 1º componente na tela, inverte suave
  // p/ chat ~30% / canvas ~70% (= layout dos apps já existentes; nada troca de lado).
  // `built` é STICKY (uma vez montado não volta — evita flicker se o fluxo esvaziar).
  const [built, setBuilt] = useState(false);
  useEffect(() => {
    if (!built && elements.some(el => isNode(el) && !isTriggerNode(el))) setBuilt(true);
  }, [elements, built]);
  // largura da faixa (p/ calcular 70% em PX — transição px->px anima suave; o canvas
  // flexGrow acompanha quadro a quadro).
  const rowRef = useRef(null);
  const [rowW, setRowW] = useState(0);
  // useLayoutEffect: mede ANTES do 1º paint → a coluna já nasce em 70% (sem animar
  // de 420px -> 70% na abertura). Depois, resize mantém a proporção.
  useLayoutEffect(() => {
    const measure = () => { if (rowRef.current) setRowW(rowRef.current.getBoundingClientRect().width); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  // "Prefiro montar manualmente": sai do hero SEM montar nada → volta ao clássico
  // (canvas amplia, card "Como quer começar?" reaparece, chat encolhe e volta ao normal).
  const [heroDismissed, setHeroDismissed] = useState(false);
  // hero ativo: automação padrão (sem `kind`), nova ou vinda do chat, ainda não montada.
  const heroMode =
    COPILOT_ENABLED && !copilotCollapsed && !readOnly && !queryKind && !built && !heroDismissed &&
    (isNew || !!buildSeedRef.current);
  const copilotWidthOverride = heroMode && rowW > 0 ? Math.round(rowW * 0.7) : null;
  // AUTOSAVE no rascunho: indicador "Salvo · há Xs" (idle | saving | saved | error).
  const [saveStatus, setSaveStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autosaveReadyRef = useRef(false); // só libera o autosave após a carga inicial assentar
  const autosaveTimer = useRef(null);
  // UNDO/REDO: pilhas de snapshots do `elements` (coalescidas). Refs p/ não ficar
  // stale no handler global de teclado. committedRef = baseline atual.
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const committedRef = useRef(null);
  const restoringRef = useRef(false);
  const recordTimer = useRef(null);
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  // reflete o tamanho das pilhas p/ habilitar/desabilitar os botões de desfazer/refazer
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHistory = () => { setCanUndo(undoRef.current.length > 0); setCanRedo(redoRef.current.length > 0); };
  const [selectedRun, setSelectedRun] = useState(null); // runId being inspected
  const [runData, setRunData] = useState(null); // { <step>: { input, output, status, ... } }
  // saída REAL de cada nó na ÚLTIMA execução (step -> data) — alimenta o picker de
  // variáveis com valores de verdade (igual ao gatilho webhook). Só leitura.
  const [lastRunSteps, setLastRunSteps] = useState({});
  const [lastRunAt, setLastRunAt] = useState(null); // quando foi a última execução (nota do picker)

  // inspect an execution: load its events and index by node step
  const onSelectRun = async run => {
    setSelectedRun(run.runId);
    try {
      const { data } = await api.get("/events", { params: { runId: run.runId } });
      const map = {};
      (data.events || []).forEach(ev => {
        const step = ev.data && ev.data.step;
        if (!step) return;
        // O status da chamada chega DEPOIS do "call.originated", em vários eventos
        // do MESMO passo (tocando → atendida → encerrada). Eles se FUNDEM na saída
        // do nó em vez de caírem na regra "o primeiro vence" — senão a aba
        // Teste & resultados mostrava só o callSid, sem o desfecho da chamada.
        // Eventos vêm em ordem cronológica, então o último status é o que vale.
        if (ev.type === "call.status" && map[step]) {
          map[step] = {
            ...map[step],
            status: ev.status,
            output: { ...(map[step].output || {}), ...((ev.data && ev.data.output) || {}) },
          };
          return;
        }
        // Desfecho ASSÍNCRONO da conversa conduzida pela IA (nó "Iniciar conversa"/
        // "Transferir para a IA"): chega depois, no mesmo passo, e se FUNDE na saída
        // do nó (started/connectorId + result/reply/done) — igual ao call.status.
        if (ev.type === "ai_result" && map[step]) {
          map[step] = {
            ...map[step],
            status: ev.status,
            output: { ...(map[step].output || {}), ...((ev.data && ev.data.output) || {}) },
          };
          return;
        }
        if (!map[step]) map[step] = { ...ev.data, status: ev.status };
      });
      setRunData(map);
    } catch (err) {
      toastError(err);
    }
  };

  const wrapperRef = useRef(null);
  const idRef = useRef(1);
  const copilotRefsRef = useRef({}); // apelido (ref) → id dos nós criados pelo copiloto
  const [copilotBusy, setCopilotBusy] = useState(false); // copiloto gerando/agindo agora
  const [copilotStatus, setCopilotStatus] = useState(""); // a frase que o chat mostra agora
  // templates de WhatsApp criados NESTA sessão (pelo copiloto/card) — só estes
  // podem ser "alterados" (apagar+recriar) em caso de conflito de nome; nomes
  // pré-existentes/de outra sessão recebem um nome novo, nunca são sobrescritos
  const sessionTemplatesRef = useRef(new Set());
  // arraste de bloco de repetição: ao mover o cabeçalho, move o bloco inteiro
  const loopDragRef = useRef(null); // { id, last:{x,y}, members:[ids] }
  // latest elements for the copilot executors (read between async tool turns)
  const elementsRef = useRef(elements);
  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);
  // ── DESTAQUE DO NÓ EM EXECUÇÃO ────────────────────────────────────────────
  // Com o canvas aberto durante uma execução, o nó da vez pulsa — SEMPRE um, e
  // só um, enquanto a execução anda. Sinais que movem o destaque:
  //   "node.running" — o backend avisa que um nó de backend COMEÇOU (só socket,
  //     não vira histórico). É o que segura o destaque no nó certo enquanto ele
  //     demora, em vez de o canvas ficar mostrando o anterior.
  //   "event"        — o evento gravado, que cobre o resto: o gatilho, os verbos
  //     de chamada servidos (say/play/gather), o resultado da coleta.
  //
  // O destaque NÃO expira por tempo. A primeira versão apagava em 4s e ficava
  // "meia boca": uma fala de 8 segundos ou uma coleta esperando resposta passam
  // disso com folga, e o canvas ficava escuro justamente durante o trecho mais
  // longo. Agora ele fica onde está até chegar o próximo sinal ou o FIM:
  //   - chamada: o call.status terminal (encerrada/falhou/ocupado/não atendida);
  //   - fluxo sem chamada: o "flow.idle" que o backend manda ao terminar.
  // O relógio de 3 minutos é só rede de segurança, para um destaque nunca ficar
  // preso para sempre se um sinal de fim se perder.
  const FIM_DE_CHAMADA = ["completed", "failed", "busy", "no-answer", "no_answer", "canceled"];
  const [nodeRodando, setNodeRodando] = useState(null);
  useEffect(() => {
    if (!numericId) return undefined;
    const socket = connectToSocket();
    socket.emit("joinRoom", "events");
    let timer = null;
    // Execuções já encerradas. Não basta APAGAR no fim: ainda chegam eventos
    // depois do desligamento (a retomada com a transcrição parcial roda nós de
    // backend, e o jambonz ainda registra o que serviu). Sem esta lista o último
    // deles reacendia um nó — o "Desligar" ficava pulsando para sempre.
    const encerrados = new Set();
    const apagar = runId => {
      if (runId) encerrados.add(runId);
      if (timer) clearTimeout(timer);
      timer = null;
      setNodeRodando(null);
    };
    const marcar = (step, runId) => {
      if (!step || (runId && encerrados.has(runId))) return;
      const mapa = stepNamesFor(elementsRef.current);
      const id = Object.keys(mapa).find(k => mapa[k] === step);
      // gatilho: o passo "trigger" não está no mapa (stepNamesFor pula o nó de
      // gatilho), então acha-se pelo próprio elemento
      const alvo = id || (step === "trigger" ? (elementsRef.current.find(isTriggerNode) || {}).id : null);
      if (!alvo) return;
      setNodeRodando(alvo);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setNodeRodando(null), 180000);
    };
    const daApp = p => p && String(p.applicationId) === String(numericId);
    const onRunning = p => { if (daApp(p)) marcar(p.step, p.runId); };
    const onIdle = p => { if (daApp(p)) apagar(p.runId); };
    const onEvent = p => {
      if (!p || p.action !== "create" || !daApp(p.event)) return;
      const d = (p.event.data || {});
      // fim da chamada: apaga E marca a execução como encerrada, senão os eventos
      // que ainda chegam depois do desligamento reacendem um nó
      if (p.event.type === "call.status") {
        const st = String((d.output && d.output.status) || d.call_status || "").toLowerCase();
        if (FIM_DE_CHAMADA.indexOf(st) >= 0) {
          apagar(p.event.runId);
          return;
        }
      }
      marcar(d.step, p.event.runId);
    };
    socket.on("node.running", onRunning);
    socket.on("flow.idle", onIdle);
    socket.on("event", onEvent);
    return () => {
      if (timer) clearTimeout(timer);
      socket.emit("leaveRoom", "events");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId]);

  // projectId estável para as tools do copiloto (runCopilotTool tem deps [])
  const projectIdRef = useRef(projectId);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  // ações atuais (atualizadas a cada render) — usadas pelas tools do copiloto sem
  // cair em closure stale (runCopilotTool tem deps [])
  const validateRef = useRef(() => []);
  const publishRef = useRef(async () => ({ ok: false }));
  // motivo da última falha de publicação ({status, message}) — o copiloto precisa
  // dele para distinguir "conserta o fluxo" de "limite de plano, não insista"
  const publishErrRef = useRef(null);
  const dialRef = useRef(async () => ({ ok: false }));
  const appIdRef = useRef(null); // id/code da app salva (p/ place_call)
  // abre o copiloto automaticamente quando veio um seed do chat (?build=)
  useEffect(() => {
    if (buildSeedRef.current && COPILOT_ENABLED) setCopilotCollapsedP(false);
  }, []);
  // vindo de "Conversas recentes": abre o copiloto já na tela
  useEffect(() => {
    if (location.state && location.state.openCopilot && COPILOT_ENABLED) setCopilotCollapsedP(false);
  }, [location.state]);

  const reseedIds = flow => {
    const maxId = flow
      .filter(isNode)
      .map(n => parseInt(String(n.id).replace(/\D/g, ""), 10))
      .filter(x => !Number.isNaN(x));
    idRef.current = (maxId.length ? Math.max(...maxId) : 0) + 1;
  };

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/applications/${applicationId}`);
        setNumericId(data.id || null);
        appIdRef.current = data.id || applicationId;
        setName(data.name || "");
        setDescription(data.description || "");
        setNotes(data.notes || "");
        setAiEditable(!!data.aiEditable);
        setInDevelopment(!!data.inDevelopment);
        setProjectId(data.projectId || null);
        setFolderId(data.folderId || null);
        setDirty(data.dirty);
        setPublishedVersion(data.publishedVersion || null);
        setWebhookId(data.webhookId || null);
        setSetup((data.definition && data.definition.setup) || null);
        if (Array.isArray(data.flow) && data.flow.length) {
          // guard against legacy/malformed nodes without a position (react-flow
          // crashes reading position.x otherwise)
          const safe = data.flow.map((el, i) =>
            isNode(el) &&
            (!el.position || typeof el.position.x !== "number")
              ? { ...el, position: { x: 250, y: 20 + i * 90 } }
              : el
          )
            .map(migrateGupshupText)
            // limpa realce transitório "+ criado" que possa ter sido salvo
            .map(el => (isNode(el) && el.data && el.data._hl ? { ...el, data: { ...el.data, _hl: false } } : el));
          setElements(safe);
          reseedIds(safe);
        }
        // libera o autosave/histórico só DEPOIS da carga assentar (senão salvaria/
        // registraria o que acabou de carregar). committedRef = baseline do undo.
        setTimeout(() => { autosaveReadyRef.current = true; committedRef.current = elementsRef.current; }, 600);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [applicationId, isNew]);

  // carrega a saída da ÚLTIMA execução (mais recente) e indexa por step, para o
  // picker de variáveis mostrar dados reais. Recarrega quando o id resolve.
  useEffect(() => {
    if (isNew) return;
    const id = numericId || applicationId;
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get("/events/runs", { params: { applicationId: id } });
        const runs = data.runs || [];
        if (!runs.length) return;
        const latest = runs.slice().sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))[0];
        const { data: ev } = await api.get("/events", { params: { runId: latest.runId } });
        const map = indexRunSteps(ev.events);
        setLastRunSteps(map);
        setLastRunAt(latest.startedAt || null);
      } catch (e) {
        /* silencioso — picker cai no schema estático */
      }
    })();
  }, [numericId, applicationId, isNew]);

  // recarrega os steps de UMA execução específica (ex.: logo após o "Testar"),
  // para o picker de variáveis mostrar a saída real dos nós sem esperar recarga.
  const refreshRunSteps = useCallback(async runId => {
    if (!runId) return;
    try {
      const { data: ev } = await api.get("/events", { params: { runId } });
      const map = indexRunSteps(ev.events);
      if (Object.keys(map).length) {
        setLastRunSteps(prev => ({ ...prev, ...map }));
        setLastRunAt(new Date().toISOString());
      }
    } catch (e) {
      /* silencioso — picker cai no schema estático */
    }
  }, []);

  // leaving guard (custom modal): the builder is a full-page route, so the main
  // exit is the back button. Dois estados distintos quando há mudanças (dirty):
  //  - NÃO SALVO (app novo ainda não persistido no backend): sair perde o que foi
  //    montado → Continuar editando / Descartar e sair / Publicar e sair.
  //  - SALVO mas NÃO PUBLICADO (rascunho já existe no backend): o rascunho está a
  //    salvo → só Publicar e sair / Não publicar (sem descartar).
  const tryLeave = async to => {
    if (!dirty) {
      history.push(to);
      return;
    }
    // id REAL no backend (null se o app novo ainda não foi salvo)
    const realId = appIdRef.current || numericId || (isNew ? null : applicationId);
    if (!realId) {
      // NÃO SALVO → sair descarta o trabalho (destrutivo)
      const r = await confirm({
        title: "Sair sem salvar?",
        body: "Este fluxo ainda não foi salvo. Se sair agora, você perde tudo o que montou.",
        actions: [
          { label: "Continuar editando", kind: "primary", value: "edit" },
          { label: "Descartar e sair", kind: "danger", value: "discard" },
          { label: "Salvar rascunho", kind: "sec", value: "save" },
        ],
      });
      if (r === "discard") history.push(to);
      else if (r === "save") {
        try {
          await saveDraft(); // persiste o rascunho (não publica) e sai
          history.push(to);
        } catch (err) {
          toastError(err);
        }
      }
      return;
    }
    // SALVO, mas NÃO PUBLICADO → rascunho já salvo; só publicar ou sair sem publicar
    const r = await confirm({
      title: "Sair sem publicar?",
      body: "Suas alterações estão salvas como rascunho e só vão ao ar quando você publicar.",
      actions: [
        { label: "Não publicar", kind: "sec", value: "leave" },
        { label: "Publicar e sair", kind: "primary", value: "publish" },
      ],
    });
    if (r === "leave") history.push(to);
    else if (r === "publish") publishAndLeave(to);
  };

  // browser tab close / reload while there are unpublished changes
  useEffect(() => {
    const onUnload = e => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  // folders of this project (naming modal's folder selector) + apps in the project
  // (duplicate-name check). Reloads when the project is known/changes.
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const [f, a] = await Promise.all([
          api.get("/folders", { params: { projectId } }),
          api.get("/applications", { params: { projectId } }),
        ]);
        setFolders(f.data.folders || f.data || []);
        setAllApps(a.data.applications || []);
      } catch (err) {
        /* non-blocking */
      }
    })();
  }, [projectId]);

  // nomes dos AGENTES usados nos nós (Transferir para a IA, Navegar, Iniciar conversa…):
  // o card mostra o agente configurado. Busca de novo quando aparece um id
  // desconhecido (ex.: o copiloto acabou de criar o agente).
  const [agentNames, setAgentNames] = useState({});
  // (também as Scripts do nó "Rodar script": data.jobId)
  const nodeAgentIds = elements
    .map(el => el && el.data && (el.data.agentId || el.data.jobId))
    .filter(Boolean)
    .map(String);
  const missingAgentKey = nodeAgentIds.filter(aid => !(aid in agentNames)).sort().join(",");
  useEffect(() => {
    if (!missingAgentKey) return;
    let alive = true;
    api.get("/applications")
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        (data.applications || []).filter(a => a.triggerType === "agent" || a.triggerType === "code_job").forEach(a => { map[String(a.id)] = a.name; });
        // id que não existe (apagado/de outra conta) fica marcado p/ não buscar em loop
        missingAgentKey.split(",").forEach(aid => { if (!(aid in map)) map[aid] = null; });
        setAgentNames(prev => ({ ...prev, ...map }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [missingAgentKey]);

  // recarrega só as pastas do projeto (após criar uma direto no modal de salvar)
  const reloadFolders = useCallback(async () => {
    if (!projectId) return;
    try {
      const f = await api.get("/folders", { params: { projectId } });
      setFolders(f.data.folders || f.data || []);
    } catch (err) {
      /* non-blocking */
    }
  }, [projectId]);

  // cria uma pasta nova ali no modal de salvar e já a seleciona como destino
  const handleNewFolder = async () => {
    if (!projectId) return;
    const nome = await prompt({ title: "Nova pasta", label: "Nome da pasta", confirmText: "Criar" });
    if (!nome || !nome.trim()) return;
    try {
      const { data } = await api.post("/folders", { name: nome.trim(), projectId });
      await reloadFolders();
      if (data && data.id) setFolderId(data.id);
    } catch (err) {
      toastError(err);
    }
  };

  // enquadra o fluxo ao abrir (gatilho sempre visível). Vale para TODOS os
  // caminhos: fluxo novo (/applications/new), criado pelo chat, ou existente.
  const fittedRef = useRef(false);
  useEffect(() => {
    if (fittedRef.current || !rfInstance || !elements.length) return;
    fittedRef.current = true;
    // maxZoom limita o zoom do fit (com poucos nós o fit iria ao máximo → fica no TETO).
    const fit = () => {
      try {
        rfInstance.fitView({ padding: 0.2, maxZoom: INITIAL_ZOOM });
        // canvas VAZIO (só o card "Como quer começar?"): o fitView o deixa no centro
        // vertical. A gente sobe ~30% da altura da tela pra ele respirar mais no topo.
        // Centraliza um ponto ABAIXO do card (em coords de fluxo) → o card sobe na tela.
        const startEl = elements.find(isTriggerNode);
        const emptyStart = elements.length <= 1 && startEl && !(startEl.data && startEl.data.triggerType);
        if (emptyStart) {
          const zph = zphRef.current;
          const wrap = wrapperRef.current;
          const ch = (wrap && wrap.getBoundingClientRect().height) || 600;
          const CW = 340, CH = 150; // tamanho aprox do card de início
          const cx = startEl.position.x + CW / 2;
          const cy = startEl.position.y + CH / 2;
          if (zph && zph.setCenter) zph.setCenter(cx, cy + (0.3 * ch) / INITIAL_ZOOM, INITIAL_ZOOM, 0);
        }
      } catch (e) { /* ignore */ }
    };
    // fit REPETIDO: num load fresco o pane/nós ainda não estão medidos no 1º tick —
    // era isso que fazia "abrir e ver área vazia". Refaz o mesmo trabalho do botão
    // centralizar até o layout assentar.
    requestAnimationFrame(fit);
    setTimeout(fit, 220);
    setTimeout(fit, 600);
  }, [rfInstance, elements]);

  // new tool/agent/scheduled: preset o gatilho conforme o kind da URL (tool/agent
  // são "sabores"; scheduled é um gatilho normal já inserido).
  useEffect(() => {
    if (!isNew || !["tool", "agent", "scheduled", "collector"].includes(queryKind)) return;
    setElements(els =>
      els.map(el =>
        isTriggerNode(el)
          ? { ...el, data: { verbKey: "__trigger__", triggerType: queryKind, ...(queryKind === "collector" ? { pollEvery: "15m" } : {}) } }
          : el
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, queryKind]);

  // new app: center the trigger node horizontally in the canvas (instead of the
  // top-left corner). Measures the canvas once it's mounted.
  useEffect(() => {
    if (!isNew || !wrapperRef.current) return;
    const w = wrapperRef.current.getBoundingClientRect().width;
    const cx = Math.max(40, Math.round(w / 2 - 120));
    setElements(els =>
      els.map(el => (el.id === "start" ? { ...el, position: { x: cx, y: 70 } } : el))
    );
    // run once on mount for a new app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  // manual connection between two existing nodes (drag from an output handle to a
  // target). One edge per output: replace any existing edge from that same handle.
  const onConnect = useCallback(
    params =>
      setElements(els => {
        const sh = params.sourceHandle || "next";
        const cleaned = els.filter(
          el => !(el.source === params.source && (el.sourceHandle || "next") === sh)
        );
        return addEdge({ ...params, ...edgeOpts }, cleaned);
      }),
    []
  );
  const onElementsRemove = useCallback(
    async toRemove => {
      const els0 = elementsRef.current;
      const baseIds = new Set((toRemove || []).filter(isNode).map(n => n.id));
      if (!baseIds.size) { setElements(e => removeElements(toRemove, e)); return; }

      // EXPANDE blocos de repetição: excluir o "Repetir para cada item" (ou o
      // "Fim da repetição") remove o BLOCO INTEIRO (cabeçalho + internos + rodapé),
      // e religa o que vinha antes com o que vinha depois. Guarda os pares p/ a ponte.
      const removeIds = new Set(baseIds);
      const blocks = []; // { headId, endId }
      const isMember = {};
      baseIds.forEach(id => {
        const n = els0.find(e => e.id === id);
        const vk = n && n.data && n.data.verbKey;
        let headId = null;
        if (vk === "loop") headId = id;
        else if (vk === "loop_end") headId = findLoopHeadForEnd(els0, id);
        if (headId && !blocks.some(b => b.headId === headId)) {
          const members = [headId, ...loopBlockIds(els0, headId)];
          members.forEach(m => { removeIds.add(m); isMember[m] = true; });
          blocks.push({ headId, endId: members[members.length - 1], inner: Math.max(0, members.length - 2) });
        }
      });

      // AVISO: se algum loop tem componentes dentro, confirma antes.
      const innerTotal = blocks.reduce((a, b) => a + b.inner, 0);
      if (innerTotal > 0) {
        const ok = await confirm({
          title: "Excluir repetição",
          message: `Isto vai excluir o "Repetir para cada item" e os ${innerTotal} componente(s) dentro dele. Deseja continuar?`,
          confirmText: "Excluir tudo",
          danger: true,
        });
        if (!ok) return;
      }

      setElements(els => {
        const bridges = [];
        // ponte do BLOCO: entrada externa do cabeçalho → saída externa do rodapé
        blocks.forEach(({ headId, endId }) => {
          const inc = els.find(e => e.target === headId && e.source && !removeIds.has(e.source));
          const out = els.find(e => e.source === endId && e.target && !removeIds.has(e.target));
          if (inc && out) bridges.push({ id: `e_${inc.source}_${inc.sourceHandle || "next"}_${out.target}`, source: inc.source, sourceHandle: inc.sourceHandle, target: out.target, ...edgeOpts });
        });
        // ponte de nó AVULSO (1 entrada / 1 saída de fora) — não-membro de bloco
        removeIds.forEach(nid => {
          if (isMember[nid]) return;
          const inc = els.find(e => e.target === nid && e.source && !removeIds.has(e.source));
          const out = els.find(e => e.source === nid && e.target && !removeIds.has(e.target));
          if (inc && out) bridges.push({ id: `e_${inc.source}_${inc.sourceHandle || "next"}_${out.target}`, source: inc.source, sourceHandle: inc.sourceHandle, target: out.target, ...edgeOpts });
        });
        const kept = els.filter(el =>
          isNode(el) ? !removeIds.has(el.id) : !(removeIds.has(el.source) || removeIds.has(el.target))
        );
        const fresh = bridges.filter(
          b => !kept.some(e => !isNode(e) && e.source === b.source && (e.sourceHandle || "next") === (b.sourceHandle || "next") && e.target === b.target)
        );
        return kept.concat(fresh);
      });
      setSelectedId(null);
      setDirty(true);
    },
    [confirm]
  );

  // react-flow is controlled here, so a drag only updates the canvas — we must
  // write the new position back into `elements` or it's lost on save/reload.
  // ao começar a arrastar o CABEÇALHO de um loop, captura os membros do bloco
  const onNodeDragStart = useCallback((_e, node) => {
    if (node && node.data && node.data.verbKey === "loop") {
      loopDragRef.current = {
        id: node.id,
        last: { ...node.position },
        total: { x: 0, y: 0 }, // delta acumulado — o frame deriva do cabeçalho VIVO
        members: loopBlockIds(elementsRef.current, node.id),
      };
    } else {
      loopDragRef.current = null;
    }
  }, []);

  // durante o arraste do cabeçalho: aplica o MESMO delta aos membros do bloco
  // (passos internos + rodapé) — eles seguem o cabeçalho ao vivo.
  const onNodeDrag = useCallback((_e, node) => {
    const ref = loopDragRef.current;
    if (!ref || !node || ref.id !== node.id || !ref.members.length) return;
    const dx = node.position.x - ref.last.x;
    const dy = node.position.y - ref.last.y;
    if (!dx && !dy) return;
    ref.last = { ...node.position };
    ref.total.x += dx;
    ref.total.y += dy;
    const ids = ref.members;
    setElements(els =>
      els.map(el => (ids.indexOf(el.id) >= 0 ? { ...el, position: { x: el.position.x + dx, y: el.position.y + dy } } : el))
    );
  }, []);

  const onNodeDragStop = useCallback((_e, node) => {
    loopDragRef.current = null;
    if (!node || !node.position) return;
    setElements(els => {
      // se o nó é interno a um loop, a posição reportada inclui o indent visual
      // (LOOP_INDENT) — desconta antes de salvar p/ não acumular deslocamento.
      const inner = loopInnerIds(els);
      const x = inner.has(node.id) ? node.position.x - LOOP_INDENT : node.position.x;
      return els.map(el => (el.id === node.id ? { ...el, position: { ...node.position, x } } : el));
    });
  }, []);

  // AUTO-LAYOUT do bloco de repetição: posiciona os passos internos + o rodapé
  // com um ESPAÇAMENTO consistente (header→1º, entre passos, último→rodapé),
  // medindo a altura real de cada card (via react-flow). A altura do container
  // se ajusta sozinha ao adicionar/remover passos. Nós após o bloco são
  // deslocados pelo delta de altura (não sobrepõem nem deixam buraco).
  useEffect(() => {
    if (!rfInstance || isNew || loopDragRef.current) return;
    let zoom = 1;
    try { const o = rfInstance.toObject(); zoom = (o && o.zoom) || 1; } catch (e) { /* default */ }
    // mede a altura REAL de cada nó pelo DOM (em coords de fluxo = px/zoom). O
    // __rf do toObject vem strippado no v9, então não dá pra confiar nele.
    const hOf = n => {
      try {
        const el = document.querySelector(`.react-flow__node[data-id="${n.id}"]`);
        if (el) { const r = el.getBoundingClientRect(); if (r.height) return r.height / zoom; }
      } catch (e) { /* ignore */ }
      return n.data.verbKey === "loop" ? 78 : n.data.verbKey === "loop_end" ? 44 : 66;
    };
    const GAP = 18; // espaço entre passos internos
    const FOOTER_EXTRA = 28; // gap extra antes do rodapé (p/ o "+" do último passo)
    const nodes = elements
      .filter(el => isNode(el) && el.data && el.position)
      .sort((a, b) => a.position.y - b.position.y);
    if (!nodes.some(n => n.data.verbKey === "loop")) return; // sem bloco, nada a fazer
    const newPos = {}; // id -> { x?, y? }
    const setP = (id, p) => { newPos[id] = { ...(newPos[id] || {}), ...p }; };
    let shift = 0, depth = 0, cursor = 0, blockX = 0;
    nodes.forEach(n => {
      const vk = n.data.verbKey;
      const h = hOf(n);
      if (depth === 0) {
        const ny = n.position.y + shift;
        if (Math.round(ny) !== Math.round(n.position.y)) setP(n.id, { y: ny });
        if (vk === "loop") { blockX = n.position.x; cursor = ny + h + GAP; depth = 1; }
      } else {
        // dentro de um bloco: alinha o X à coluna do cabeçalho (header/footer/frame
        // batem) e empilha o Y com GAP consistente. O rodapé ganha um gap extra
        // p/ o "+" do último passo não sobrepor.
        if (Math.round(n.position.x) !== Math.round(blockX)) setP(n.id, { x: blockX });
        const ny = vk === "loop_end" ? cursor + FOOTER_EXTRA : cursor;
        if (Math.round(ny) !== Math.round(n.position.y)) setP(n.id, { y: ny });
        if (vk === "loop") { cursor = ny + h + GAP; depth += 1; }
        else if (vk === "loop_end") {
          depth -= 1;
          if (depth === 0) shift = ny - n.position.y; // propaga a mudança de altura
          cursor = ny + h + GAP;
        } else {
          cursor = ny + h + GAP;
        }
      }
    });
    if (Object.keys(newPos).length) {
      setElements(els =>
        els.map(el => {
          const p = newPos[el.id];
          if (!p) return el;
          return { ...el, position: { x: p.x != null ? p.x : el.position.x, y: p.y != null ? p.y : el.position.y } };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, rfInstance, isNew]);

  // same for dragging a multi-selection (several nodes at once)
  const onSelectionDragStop = useCallback((_e, nodes) => {
    if (!Array.isArray(nodes)) return;
    const moved = {};
    nodes.forEach(n => {
      if (n && n.position) moved[n.id] = n.position;
    });
    setElements(els =>
      els.map(el => (moved[el.id] ? { ...el, position: moved[el.id] } : el))
    );
  }, []);

  const onDragOver = e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = e => {
    e.preventDefault();
    const verbKey = e.dataTransfer.getData("application/numera-verb");
    const def = VERBS[verbKey];
    if (!def || !rfInstance) return;
    const bounds = wrapperRef.current.getBoundingClientRect();
    const position = rfInstance.project({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
    });
    const id = `n_${idRef.current++}`;
    const data = { verbKey, ...JSON.parse(JSON.stringify(def.defaults || {})) };
    const node = {
      id,
      type: def.isWaMenu ? "waMenuNode" : def.isIvr ? "ivrNode" : def.isBranch ? "branchNode" : "verbNode",
      position,
      data,
    };
    setElements(es => es.concat(node));
    setSelectedId(id);
  };

  // "+" leg on a node -> open the node picker for that output
  const onAddAfter = (sourceId, sourceHandle) =>
    setPendingAdd({ sourceId, sourceHandle });

  // create the picked node below the source and connect the output to it
  const createNodeAfter = verbKey => {
    const def = VERBS[verbKey];
    if (!def || !pendingAdd) return;
    const { sourceId, sourceHandle } = pendingAdd;
    const source = elements.find(el => el.id === sourceId);
    const basePos = source && source.position ? source.position : { x: 260, y: 20 };
    // spread multi-output nodes (branch / schedule / amd) horizontally by the
    // output's index, so siblings don't spawn stacked on the same x
    const sdef = source && source.data ? VERBS[source.data.verbKey] : null;
    const outs = sdef && sdef.isBranch
      ? [...((source.data.caseKeys) || []), "default", "timeout"]
      : sdef && sdef.isWaMenu
      ? [...((source.data.options) || []).filter(Boolean), "default"]
      : sdef && sdef.isIvr
      ? [...((source.data.digits) || []), "noinput"]
      : ((sdef && sdef.handles) || []).map(h => h.id);
    const hIdx = outs.indexOf(sourceHandle);
    const n = outs.length || 1;
    const dx = n > 1 && hIdx >= 0 ? Math.round((hIdx - (n - 1) / 2) * 240) : 0;
    const id = `n_${idRef.current++}`;
    // INSERÇÃO NO MEIO: se a saída já levava a um alvo (ex.: dentro de um loop,
    // a saída ia pro "Fim da repetição"), insere ENTRE: source → novo → alvo.
    // Posiciona o novo entre os dois em Y, p/ o auto-layout tratá-lo como interno.
    const oldEdge = elements.find(el => el.source === sourceId && (el.sourceHandle || "next") === sourceHandle);
    let oldTarget = oldEdge ? elements.find(el => el.id === oldEdge.target && isNode(el)) : null;
    // loop vazio/desconectado: conecta o novo passo ao loop_end do bloco, p/ ele
    // nascer DENTRO do container (e não solto depois do rodapé).
    if (!oldTarget && source && source.data && source.data.verbKey === "loop") {
      const block = loopBlockIds(elements, sourceId); // [..internos, loop_end]
      const endId = block.length ? block[block.length - 1] : null;
      oldTarget = endId ? elements.find(el => el.id === endId && isNode(el)) : null;
    }
    // LOOP: nasce JÁ FECHADO — cria o "Fim da repetição" (loop_end) junto, para o
    // container (frame tintado) aparecer de imediato e os nós adicionados caírem
    // ENTRE o cabeçalho e o rodapé (dentro do bloco), não soltos depois dele.
    if (verbKey === "loop") {
      const endId = `n_${idRef.current++}`;
      const loopY = oldTarget && oldTarget.position ? Math.round((basePos.y + oldTarget.position.y) / 2) : basePos.y + 150;
      const loopNode = {
        id,
        type: "verbNode",
        position: { x: basePos.x + dx, y: loopY },
        data: { verbKey: "loop", ...JSON.parse(JSON.stringify(def.defaults || {})) },
      };
      const endNode = {
        id: endId,
        type: "verbNode",
        position: { x: basePos.x + dx, y: loopY + 130 },
        data: { verbKey: "loop_end" },
      };
      const loopEdges = [
        { id: `e_${sourceId}_${sourceHandle}_${id}`, source: sourceId, sourceHandle, target: id, ...edgeOpts },
        { id: `e_${id}_next_${endId}`, source: id, sourceHandle: "next", target: endId, ...edgeOpts },
      ];
      if (oldTarget) loopEdges.push({ id: `e_${endId}_next_${oldTarget.id}`, source: endId, sourceHandle: "next", target: oldTarget.id, ...edgeOpts });
      setElements(es =>
        es
          .filter(el => !(el.source === sourceId && (el.sourceHandle || "next") === sourceHandle))
          .concat([loopNode, endNode, ...loopEdges])
      );
      setSelectedId(id);
      setPendingAdd(null);
      return;
    }

    const ny =
      oldTarget && oldTarget.position
        ? Math.round((basePos.y + oldTarget.position.y) / 2)
        : basePos.y + 150;
    const node = {
      id,
      type: def.isWaMenu ? "waMenuNode" : def.isIvr ? "ivrNode" : def.isBranch ? "branchNode" : "verbNode",
      position: { x: basePos.x + dx, y: ny },
      data: { verbKey, ...JSON.parse(JSON.stringify(def.defaults || {})) },
    };
    const edges = [
      { id: `e_${sourceId}_${sourceHandle}_${id}`, source: sourceId, sourceHandle, target: id, ...edgeOpts },
    ];
    if (oldTarget) {
      edges.push({ id: `e_${id}_next_${oldTarget.id}`, source: id, sourceHandle: "next", target: oldTarget.id, ...edgeOpts });
    }
    setElements(es =>
      // replace any existing edge from this output (one edge per handle)
      es
        .filter(el => !(el.source === sourceId && (el.sourceHandle || "next") === sourceHandle))
        .concat([node, ...edges])
    );
    setSelectedId(id);
    setPendingAdd(null);
  };

  // "Repetir esta lista com X": a partir do ícone de lista de um nó, cria um
  // "Repetir para cada item" JÁ com o componente escolhido DENTRO e o campo
  // "lista a percorrer" pré-preenchido com o token da lista daquele nó.
  const createIterateNode = verbKey => {
    const innerDef = VERBS[verbKey];
    if (!innerDef || !pendingIterate) return;
    const { sourceId, listToken } = pendingIterate;
    const source = elements.find(el => el.id === sourceId);
    const basePos = source && source.position ? source.position : { x: 260, y: 20 };
    // reconecta o que vinha depois da origem, agora depois do bloco de repetição
    const oldEdge = elements.find(el => el.source === sourceId && (el.sourceHandle || "next") === "next");
    const oldTarget = oldEdge ? elements.find(el => el.id === oldEdge.target && isNode(el)) : null;

    const loopId = `n_${idRef.current++}`;
    const innerId = `n_${idRef.current++}`;
    const endId = `n_${idRef.current++}`;
    const x = basePos.x;
    const y0 = basePos.y + 150;
    const loopNode = { id: loopId, type: "verbNode", position: { x, y: y0 }, data: { verbKey: "loop", list: listToken, itemAs: "item" } };
    const innerType = innerDef.isWaMenu ? "waMenuNode" : innerDef.isIvr ? "ivrNode" : innerDef.isBranch ? "branchNode" : "verbNode";
    const innerNode = { id: innerId, type: innerType, position: { x: x + LOOP_INDENT, y: y0 + 120 }, data: { verbKey, ...JSON.parse(JSON.stringify(innerDef.defaults || {})), _hl: true } };
    const endNode = { id: endId, type: "verbNode", position: { x, y: y0 + 240 }, data: { verbKey: "loop_end" } };
    const iterEdges = [
      { id: `e_${sourceId}_next_${loopId}`, source: sourceId, sourceHandle: "next", target: loopId, ...edgeOpts },
      { id: `e_${loopId}_next_${innerId}`, source: loopId, sourceHandle: "next", target: innerId, ...edgeOpts },
      { id: `e_${innerId}_next_${endId}`, source: innerId, sourceHandle: "next", target: endId, ...edgeOpts },
    ];
    if (oldTarget) iterEdges.push({ id: `e_${endId}_next_${oldTarget.id}`, source: endId, sourceHandle: "next", target: oldTarget.id, ...edgeOpts });
    setElements(es =>
      es
        .filter(el => !(el.source === sourceId && (el.sourceHandle || "next") === "next"))
        .concat([loopNode, innerNode, endNode, ...iterEdges])
    );
    setSelectedId(innerId);
    setPendingIterate(null);
  };

  // resultado de um TESTE de nó (ex.: Enviar HTTP) entra no mesmo mapa da última
  // execução real (lastRunSteps) → fica disponível no picker de variáveis como se
  // o fluxo tivesse rodado. Não persiste nada; só alimenta o picker localmente.
  const onNodeTested = useCallback((nodeId, output) => {
    const name = stepNamesFor(elements)[nodeId];
    if (!name || output == null) return;
    setLastRunSteps(prev => ({ ...prev, [name]: { step: name, output, status: "success" } }));
    setLastRunAt(Date.now());
  }, [elements]);

  const selectedNode = elements.find(el => el.id === selectedId && isNode(el)) || null;

  const updateNodeData = newData => {
    setElements(els =>
      els.map(el => (el.id === selectedId ? { ...el, data: newData } : el))
    );
  };

  const swapSelectedNode = verbKey => {
    const def = VERBS[verbKey];
    if (!def || !selectedNode || isTriggerNode(selectedNode)) return;
    const data = { verbKey, ...JSON.parse(JSON.stringify(def.defaults || {})) };
    const outs = def.isBranch
      ? [...(data.caseKeys || []), "default", "timeout"]
      : def.isWaMenu
      ? [...(data.options || []).filter(Boolean), "default"]
      : def.isIvr
      ? [...(data.digits || []), "noinput"]
      : def.handles && def.handles.length
      ? def.handles.map(h => h.id)
      : def.terminal
      ? []
      : ["next"];
    setElements(es =>
      es
        .filter(el => !(el.source === selectedNode.id && !outs.includes(el.sourceHandle || "next")))
        .map(el =>
          el.id === selectedNode.id
            ? {
                ...el,
                type: def.isWaMenu ? "waMenuNode" : def.isIvr ? "ivrNode" : def.isBranch ? "branchNode" : "verbNode",
                data,
              }
            : el
        )
    );
    setDirty(true);
  };

  const [swapPickerOpen, setSwapPickerOpen] = useState(false);

  const deleteSelected = () => {
    if (!selectedNode || isTriggerNode(selectedNode)) return; // trigger is fixed
    onElementsRemove([selectedNode]);
  };

  // menu de contexto dos blocos: renomear / apagar
  const renameNodeById = async id => {
    setCtxMenu(null);
    const node = elementsRef.current.find(el => el.id === id);
    if (!node) return;
    const def = node.data && node.data.verbKey && VERBS[node.data.verbKey];
    const current = (node.data && node.data.label) || (def && def.title) || "";
    const novo = await prompt({ title: "Renomear bloco", label: "Nome", defaultValue: current, confirmText: "Renomear" });
    if (novo == null) return;
    setElements(els => els.map(el => (el.id === id ? { ...el, data: { ...el.data, label: novo.trim() || undefined } } : el)));
    setDirty(true);
  };
  const removeNodeById = id => {
    setCtxMenu(null);
    const node = elementsRef.current.find(el => el.id === id);
    if (!node || isTriggerNode(node)) return;
    onElementsRemove([node]);
  };

  // ---- multi-selection (Shift+click / Shift+drag box) ----
  const onSelectionChange = useCallback(els => {
    setSelectedIds((els || []).filter(isNode).map(n => n.id));
  }, []);
  const multiSelected = selectedIds.length > 1;
  // selected nodes eligible for bulk ops (the trigger is fixed, never bulk-acted)
  const bulkNodes = () =>
    elements.filter(el => selectedIds.includes(el.id) && isNode(el) && !isTriggerNode(el));

  const bulkDelete = async () => {
    const nodes = bulkNodes();
    if (!nodes.length) return;
    if (!(await confirm({ title: "Excluir nós", message: `Excluir ${nodes.length} nó(s) selecionado(s)? Esta ação não pode ser desfeita.`, confirmText: "Excluir", danger: true }))) return;
    onElementsRemove(nodes);
    setSelectedIds([]);
  };

  // toggle: if every selected node is already disabled -> enable all, else disable all
  const bulkToggleDisable = () => {
    const nodes = bulkNodes();
    if (!nodes.length) return;
    const val = !nodes.every(n => n.data.disabled);
    const ids = new Set(nodes.map(n => n.id));
    setElements(els =>
      els.map(el => (ids.has(el.id) ? { ...el, data: { ...el.data, disabled: val } } : el))
    );
  };
  const bulkAllDisabled = (() => {
    const nodes = bulkNodes();
    return nodes.length > 0 && nodes.every(n => n.data.disabled);
  })();

  // extract trigger type/config/direction from the trigger node.
  // `sample` is the test export — kept on the node, NOT part of triggerConfig.
  const getTrigger = (els = elementsRef.current) => {
    const tn = els.find(isTriggerNode);
    if (!tn || !tn.data.triggerType) {
      return { triggerType: null, triggerConfig: null, direction: "inbound" };
    }
    const { verbKey, triggerType, sample, ...cfg } = tn.data;
    return {
      triggerType,
      triggerConfig: cfg,
      direction: triggerDirection(triggerType),
    };
  };

  const handlePickTrigger = key => {
    setElements(els =>
      els.map(el =>
        isTriggerNode(el)
          ? { ...el, data: { verbKey: "__trigger__", triggerType: key } }
          : el
      )
    );
    setDirty(true);
    setTriggerModalOpen(false);
  };

  // set the selected event as the trigger's steps.trigger sample and persist it
  const setTriggerSample = async sample => {
    const newElements = elements.map(el =>
      isTriggerNode(el) ? { ...el, data: { ...el.data, sample } } : el
    );
    setElements(newElements);
    if (isNew) return; // nothing to persist yet
    try {
      const { definition } = compile(newElements, {
        name,
        description,
        trigger: getTrigger(newElements).direction,
      });
      const { triggerType, triggerConfig, direction } = getTrigger(newElements);
      await api.put(`/applications/${applicationId}`, {
        name,
        description,
        trigger: direction,
        triggerType,
        triggerConfig,
        flow: newElements,
        definition,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const buildDefinition = () =>
    compile(elementsRef.current, { name: nameRef.current, description: descRef.current, trigger: getTrigger().direction });

  // tester: salva o rascunho (garante id) e devolve {id, definition atual}
  const ensureSavedForTest = async () => {
    const id = await saveDraft();
    const { definition } = buildDefinition();
    return { id, definition };
  };
  // parâmetros de entrada declarados no gatilho da Ferramenta
  const toolInputParams = (() => {
    const tn = elements.find(isTriggerNode);
    return (tn && tn.data && tn.data.inputParams) || [];
  })();

  // auto-salva o rascunho após o copiloto mexer na estrutura, para o fluxo
  // persistir no servidor (e aparecer em "Conversas recentes"). Só quando o app
  // JÁ existe (não isNew) — evita POST + troca de rota no meio da montagem.
  const autoSavingRef = useRef(false);
  const autoSaveDraft = async () => {
    if (autoSavingRef.current || readOnly) return;
    // app NOVO: só cria quando já há conteúdo REAL (um nó além do gatilho) — evita
    // criar rascunho vazio a cada "oi". Depois de criado, cai no update normal.
    if (isNew && !appIdRef.current && !elementsRef.current.some(el => isNode(el) && !isTriggerNode(el))) return;
    autoSavingRef.current = true;
    setSaveStatus("saving");
    try {
      const savedId = await saveDraft();
      setSaveStatus("saved");
      setLastSavedAt(Date.now());
      // AUTORIA: quem pôs o 1º nó/gatilho num fluxo vazio foi o copiloto → o fluxo é
      // "dele" (aiEditable), e ele altera sem pedir permissão também depois de
      // recarregar/reabrir (antes isso só valia enquanto a tela ficava aberta).
      const id = savedId || appIdRef.current || (!isNew ? applicationId : null);
      if (id && copilotMadeFlowRef.current && !aiEditableMarkedRef.current) {
        aiEditableMarkedRef.current = true;
        api.put(`/applications/${id}`, { aiEditable: true }).then(() => setAiEditable(true)).catch(() => { aiEditableMarkedRef.current = false; });
      }
    } catch (e) {
      setSaveStatus("error");
    } finally {
      autoSavingRef.current = false;
    }
  };
  // dispara o autosave (debounced) a cada edição, depois que a carga inicial assentou
  const scheduleAutosave = () => {
    if (!autosaveReadyRef.current || readOnly) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { autoSaveDraft(); }, 1200);
  };
  // TUDO que o usuário mexe (nós, conexões, posição, nome) vai pro rascunho sozinho.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { scheduleAutosave(); return () => clearTimeout(autosaveTimer.current); }, [elements, name]);
  // libera o autosave/histórico p/ app NOVO já no início (o guard evita rascunho vazio)
  useEffect(() => { if (isNew) { autosaveReadyRef.current = true; committedRef.current = elementsRef.current; } }, [isNew]);
  // HISTÓRICO (undo/redo): registra snapshots do `elements`, coalescendo rajadas
  // (um arrasto vira 1 entrada). Ignora as mudanças que vieram de um undo/redo.
  useEffect(() => {
    if (!autosaveReadyRef.current) return;
    if (restoringRef.current) { restoringRef.current = false; committedRef.current = elements; return; }
    clearTimeout(recordTimer.current);
    recordTimer.current = setTimeout(() => {
      const base = committedRef.current;
      if (base && base !== elements) {
        undoRef.current = [...undoRef.current.slice(-49), base];
        redoRef.current = [];
        syncHistory();
      }
      committedRef.current = elements;
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);
  const undo = useCallback(() => {
    if (readOnlyRef.current || !undoRef.current.length) return;
    clearTimeout(recordTimer.current);
    const target = undoRef.current[undoRef.current.length - 1];
    undoRef.current = undoRef.current.slice(0, -1);
    redoRef.current = [...redoRef.current.slice(-49), elementsRef.current];
    restoringRef.current = true;
    committedRef.current = target;
    setElements(target);
    setSelectedId(null);
    setDirty(true); // o autosave dispara sozinho pela mudança de `elements`
    syncHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const redo = useCallback(() => {
    if (readOnlyRef.current || !redoRef.current.length) return;
    clearTimeout(recordTimer.current);
    const target = redoRef.current[redoRef.current.length - 1];
    redoRef.current = redoRef.current.slice(0, -1);
    undoRef.current = [...undoRef.current.slice(-49), elementsRef.current];
    restoringRef.current = true;
    committedRef.current = target;
    setElements(target);
    setSelectedId(null);
    setDirty(true);
    syncHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Ctrl/Cmd+Z = desfazer · Ctrl/Cmd+Shift+Z (ou Ctrl+Y) = refazer. Nos campos de
  // texto deixa o undo NATIVO do navegador agir.
  useEffect(() => {
    const onKey = e => {
      const t = e.target || {};
      const tag = (t.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t.isContentEditable) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = (e.key || "").toLowerCase();
      if (k === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (k === "y") { e.preventDefault(); redo(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [undo, redo]);
  // DESCARTAR RASCUNHO: joga fora as alterações não publicadas e volta pra versão
  // publicada. O gatilho é extraído do flow publicado (getTrigger) e enviado junto.
  const [draftMenuAnchor, setDraftMenuAnchor] = useState(null);
  const [publishDiff, setPublishDiff] = useState(null); // { version, added, removed, changed, ... }
  const discardDraft = async () => {
    if (publishedVersion == null) return;
    const ok = await confirm({
      title: "Descartar rascunho",
      message: `As alterações não publicadas serão descartadas e o fluxo volta para a versão publicada v${publishedVersion}. Não dá para desfazer.`,
      confirmText: "Descartar",
      danger: true,
    });
    if (!ok) return;
    const id = appIdRef.current || numericId || applicationId;
    try {
      const { data: ver } = await api.get(`/applications/${id}/versions/${publishedVersion}`);
      const flow = Array.isArray(ver.flow) ? ver.flow : [];
      const { triggerType, triggerConfig, direction } = getTrigger(flow);
      await api.post(`/applications/${id}/discard-draft`, { triggerType, triggerConfig, trigger: direction });
      // aplica local (sem registrar no histórico como edição do usuário)
      restoringRef.current = true;
      committedRef.current = flow;
      undoRef.current = [];
      redoRef.current = [];
      syncHistory();
      setElements(flow);
      reseedIds(flow);
      setSelectedId(null);
      setDirty(false);
      setSaveStatus("idle");
      toast.success("Rascunho descartado — voltou para a versão publicada.");
    } catch (e) {
      toastError(e);
    }
  };
  // "Salvo · há Xs": re-render periódico enquanto está salvo, p/ o tempo andar
  const [saveTick, setSaveTick] = useState(0);
  useEffect(() => {
    if (saveStatus !== "saved") return undefined;
    const iv = setInterval(() => setSaveTick(t => t + 1), 10000);
    return () => clearInterval(iv);
  }, [saveStatus]);
  const relSavedText = () => {
    void saveTick; // recomputa a cada tick
    if (!lastSavedAt) return "";
    const s = Math.max(0, Math.round((Date.now() - lastSavedAt) / 1000));
    if (s < 5) return "agora";
    if (s < 60) return `há ${s}s`;
    const m = Math.round(s / 60);
    return m < 60 ? `há ${m}min` : "há mais de 1h";
  };

  // persists the draft, creating the app if new; returns its id
  const saveDraft = async () => {
    const { definition } = buildDefinition();
    // preserva o guia "Primeiros passos" (não é recompilado pelo build)
    if (setup) definition.setup = setup;
    const { triggerType, triggerConfig, direction } = getTrigger();
    const payload = {
      name: nameRef.current,
      description: descRef.current,
      trigger: direction,
      triggerType,
      triggerConfig,
      flow: elementsRef.current,
      definition,
      projectId,
      folderId,
    };
    // idempotente: se JÁ criamos o app (appIdRef) num autosave anterior, atualiza —
    // mesmo que `isNew` ainda esteja "velho" no closure até o re-render da rota.
    const existingId = appIdRef.current || (!isNew ? applicationId : null);
    if (!existingId) {
      const { data } = await api.post("/applications", payload);
      history.replace(`/applications/${data.code || data.id}`);
      setDirty(true);
      setWebhookId(data.webhookId || null);
      appIdRef.current = data.id;
      return data.id;
    }
    const { data } = await api.put(`/applications/${existingId}`, payload);
    // o backend decide o dirty: mudança só de POSIÇÃO no canvas não marca
    // "alterações não publicadas" (posição não entra na definition compilada)
    setDirty(data.dirty != null ? data.dirty : true);
    setWebhookId(data.webhookId || null);
    appIdRef.current = data.id || applicationId;
    return applicationId;
  };

  const webhookUrl = webhookId
    ? `${window.location.origin}/w/${webhookId}`
    : null;

  // auto-carrega a ÚLTIMA execução do gatilho como sample p/ o picker de variáveis
  // sempre sugerir os campos REAIS da última execução — mesmo editando outro nó, e
  // para QUALQUER gatilho com dados dinâmicos (webhook/evento → {event}, ligação
  // entrante → {call}, etc.). O evento do gatilho de qualquer tipo tem
  // data.step === "trigger". Só roda 1x e só quando ainda não há sample capturado
  // (seleção manual de evento continua tendo precedência). Não persiste nem marca
  // dirty: é só uma dica em memória para o picker.
  const triggerSampleAutoRef = useRef(false);
  useEffect(() => {
    if (triggerSampleAutoRef.current || isNew || !numericId) return;
    const tn = elements.find(isTriggerNode);
    if (!tn || !tn.data || !tn.data.triggerType) return; // espera o fluxo carregar
    // 'tool' deriva dos parâmetros do próprio fluxo, não de eventos
    // tool deriva dos params; data_collector deriva de um registro do COLETOR
    // escolhido (efeito próprio abaixo) — nenhum dos dois vem das execuções do app
    if (tn.data.triggerType === "tool" || tn.data.triggerType === "data_collector") { triggerSampleAutoRef.current = true; return; }
    if (tn.data.sample) { triggerSampleAutoRef.current = true; return; } // já tem amostra
    triggerSampleAutoRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/events", { params: { applicationId: numericId } });
        const ev = (data.events || []).find(e => e.data && e.data.step === "trigger");
        if (!ev) return;
        // output do gatilho: {event:{...}} (webhook) | {call:{...}} (ligação) | ...
        const out = ev.data.output || ev.data;
        setElements(els =>
          els.map(el => (isTriggerNode(el) ? { ...el, data: { ...el.data, sample: out } } : el))
        );
      } catch (e) {
        /* silencioso — picker cai no schema/genérico */
      }
    })();
  }, [isNew, numericId, elements]);

  // gatilho "Coletor de dados": ao escolher o coletor, buscamos UM registro da
  // última execução dele (a saída do nó "Retornar ao coletor") e guardamos como
  // sample do gatilho, para o picker sugerir steps.trigger.event.<coluna> (id,
  // nome, email…). Só dica de UI: `sample` não vai pro triggerConfig (é removido
  // no getTrigger). Re-busca quando o coletor escolhido muda.
  const collectorSampleRef = useRef(null);
  useEffect(() => {
    const tn = elements.find(isTriggerNode);
    if (!tn || !tn.data || tn.data.triggerType !== "data_collector") return;
    const cid = tn.data.collectorId;
    if (!cid || collectorSampleRef.current === cid) return;
    collectorSampleRef.current = cid;
    (async () => {
      try {
        const { data: runsData } = await api.get("/events/runs", { params: { applicationId: cid } });
        const runs = runsData.runs || [];
        if (!runs.length) return;
        const latest = runs.slice().sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))[0];
        const { data: evData } = await api.get("/events", { params: { runId: latest.runId } });
        const cr = (evData.events || []).find(e => e.data && e.data.output && Array.isArray(e.data.output.rows));
        const rec = cr && cr.data.output.rows[0];
        if (rec && typeof rec === "object") {
          setElements(els => els.map(el => (isTriggerNode(el) ? { ...el, data: { ...el.data, sample: rec } } : el)));
        }
      } catch (e) {
        /* silencioso — picker cai no genérico */
      }
    })();
  }, [elements]);

  // variable scope for {{steps.*}} pickers. The trigger exposes a FIXED canonical
  // envelope (steps.trigger.event.{method,path,query,headers,body,...}) that always
  // matches the runtime scope built by HandleWebhookService — never derived from the
  // sample's shape. The captured sample only fills in the dynamic field NAMES inside
  // body/query/headers (old log-shape samples are normalized via output.event).
  const triggerSample = (() => {
    const tn = elements.find(isTriggerNode);
    // inbound call: the trigger exposes the call metadata jambonz sends
    // (steps.trigger.call.*) — matches the scope serveNode builds at runtime.
    if (tn && tn.data && tn.data.triggerType === "inbound_call") {
      const def = {
        from: "1199998888",
        to: "1930950000",
        sid: "call-sid",
        direction: "inbound",
        callId: "call-id",
        sipStatus: 200,
        sipReason: "OK",
        callStatus: "in-progress",
        accountSid: "account-sid",
        traceId: "trace-id",
        fsSipAddress: "10.0.0.1:5070",
        // preenchidos só quando o gatilho "Gravar chamada" está ligado (o público,
        // só se "Tornar pública"). Os links funcionam quando a gravação sobe (logo
        // após a chamada). Ver o gatilho de chamada.
        recording: true,
        recordingUrl: "https://studio.numera.io/api/recordings/call-sid",
        recordingPublicUrl: "https://studio.numera.io/api/recordings/call-sid/public?u=1&exp=...&sig=...",
      };
      // mescla a ligação REAL da última execução (auto-carregada como sample.call)
      // sobre o exemplo — o picker mostra o from/to/etc. que realmente chegaram
      const real = tn.data.sample && tn.data.sample.call;
      return { call: { ...def, ...(real || {}) } };
    }
    // ferramenta: os parâmetros que a IA preenche viram steps.trigger.input.*
    if (tn && tn.data && tn.data.triggerType === "tool") {
      const params = (tn.data.inputParams || []).filter(p => p && p.name);
      const input = {};
      params.forEach(p => {
        input[p.name] = p.type === "number" ? 0 : p.type === "boolean" ? true : "exemplo";
      });
      return { input };
    }
    // Coletor de dados: cada registro novo chega como steps.trigger.event.<coluna>.
    // O sample é um registro real da última execução do coletor escolhido.
    if (tn && tn.data && tn.data.triggerType === "data_collector") {
      const rec = tn.data.sample;
      return { event: rec && typeof rec === "object" && !Array.isArray(rec) ? rec : {} };
    }
    const s = tn && tn.data && tn.data.sample;
    const rawEv = s && s.event;
    const ev = rawEv && rawEv.output && rawEv.output.event ? rawEv.output.event : rawEv;
    // WhatsApp (Gupshup) inbound: expose the message shape we dispatch so the
    // picker suggests {{steps.trigger.event.body.from}}, .text, .name, etc.
    // without needing a captured test sample.
    const gupshupBody =
      tn && tn.data && tn.data.triggerType === "event.gupshup.ao_receber_mensagem"
        ? {
            from: "5519999998888",
            name: "Fulano de Tal",
            text: "Olá!",
            type: "text",
            messageId: "wamid.HBgN...",
            timestamp: "1781222422",
            message: { from: "5519999998888", id: "wamid.HBgN...", type: "text", text: { body: "Olá!" }, timestamp: "1781222422" },
            contact: { profile: { name: "Fulano de Tal" }, wa_id: "5519999998888" },
            metadata: { display_phone_number: "551930950000", phone_number_id: "1203274539533684" },
          }
        : null;
    return {
      event: {
        method: (ev && ev.method) || "POST",
        path: (ev && ev.path) || "/",
        query: (ev && ev.query) || {},
        headers: (ev && ev.headers) || {},
        body: (ev && ev.body) || gupshupBody || {},
        client_ip: (ev && ev.client_ip) || "",
        url: (ev && ev.url) || "",
      },
    };
  })();
  // Saídas configuradas no agente de um nó "Transferir para IA" (voz ou WhatsApp):
  // do próprio agente (lista do projeto) e, se não achar, do cache no nó (_outputs)
  // — nós criados pelo copiloto não têm o cache (STU-155).
  const agentOutputNames = n => {
    const id = n && n.data && Number(n.data.agentId);
    const ag = id ? allApps.find(a => a.id === id) : null;
    const outs = ag && ag.triggerConfig && Array.isArray(ag.triggerConfig.outputs) ? ag.triggerConfig.outputs : null;
    if (outs) return outs.map(o => o && o.name).filter(Boolean);
    return (n && n.data && n.data._outputs) || [];
  };
  // scope also exposes each node's exports under steps.<node> so a field can
  // reference values produced by other nodes (e.g. {{steps.gather.transcript}}).
  const nodeSteps = (() => {
    const stepMap = stepNamesFor(elements);
    const out = {};
    elements.forEach(n => {
      const def = n.data && n.data.verbKey && VERBS[n.data.verbKey];
      if (!def) return;
      const name = stepMap[n.id];
      let base;
      if (def.exports && def.exports.length) {
        base = {};
        def.exports.forEach(e => {
          base[e.key] = e.sample != null ? e.sample : "";
        });
        // WhatsApp: o resultado do agente vem em result.<campo>
        if (n.data.verbKey === "transfer_to_ai") {
          const outs = agentOutputNames(n);
          if (outs.length) base.result = Object.fromEntries(outs.map(k => [k, ""]));
        }
      } else if (def.exportsDynamic) {
        // output shape decided at runtime (e.g. the code node's return) — expose
        // the node so it can be referenced (user appends the field path). Para o
        // "Transferir para IA" os campos de saída do agente são cacheados em
        // data._outputs ao escolher o agente, então já mostramos os nomes.
        const outs = agentOutputNames(n);
        base = outs.length ? Object.fromEntries(outs.map(k => [k, ""])) : {};
      }
      // mescla a SAÍDA REAL da última execução por cima do schema — dados de
      // verdade ajudam o usuário a montar (e revelam campos de nós dinâmicos
      // como o "Executar código", cujo schema é vazio).
      const real = lastRunSteps[name] && lastRunSteps[name].output;
      if (real && typeof real === "object" && !Array.isArray(real)) {
        out[name] = { ...(base || {}), ...real };
      } else if (base !== undefined) {
        out[name] = base;
      }
    });
    return out;
  })();
  // contexto da sessão ({{session.*}}): sugere as chaves dos nós "Guardar no
  // contexto" deste fluxo (em outros fluxos/ferramentas, digita-se à mão).
  const sessionScope = (() => {
    const out = {};
    elements.forEach(n => {
      if (isNode(n) && n.data && n.data.verbKey === "context_set" && n.data.key) out[n.data.key] = "";
    });
    return out;
  })();
  // item atual de um nó "Repetir para cada item" ({{item.*}}, {{item_number}}):
  // top-level no scope para o picker sugerir (a forma do item é definida pela API,
  // então o usuário completa o caminho .telefone/.nome).
  const loopScope = (() => {
    const out = {};
    elements.forEach(n => {
      if (isNode(n) && n.data && n.data.verbKey === "loop") {
        const nm = (n.data.itemAs || "item").trim() || "item";
        out[nm] = {};
        out[`${nm}_number`] = 1;
      }
    });
    return out;
  })();
  const scope = { steps: { trigger: triggerSample, ...nodeSteps }, session: sessionScope, ...loopScope };

  // when inspecting an execution, the in/out data for the selected node
  const selectedStep = selectedNode
    ? isTriggerNode(selectedNode)
      ? "trigger"
      : stepNamesFor(elements)[selectedNode.id]
    : null;
  const runStepData = runData && selectedStep ? runData[selectedStep] : null;

  // "Contexto disponível" da execução para o nó selecionado: o objeto `steps` que
  // os templates {{steps.<nó>.<campo>}} enxergam — a saída de TODOS os nós da run
  // (menos o próprio nó) + o gatilho. Reconstruído dos eventos já carregados em
  // runData, sem mudar o backend. Ajuda a ver o dado que CHEGOU (independente de
  // ter sido consumido) e a flagrar mapeamento apontando pro step errado.
  const runContext = (() => {
    if (!runData) return null;
    const steps = {};
    Object.keys(runData).forEach(step => {
      if (step === selectedStep) return; // o nó não referencia a si mesmo
      const sd = runData[step] || {};
      steps[step] =
        sd.output !== undefined ? sd.output
          : sd.event !== undefined ? { event: sd.event }
          : sd.input !== undefined ? sd.input
          : sd;
    });
    return Object.keys(steps).length ? { steps } : null;
  })();

  // ── Picker de Variáveis: modelo de GRUPOS (1 por nó de origem) ──────────────
  // Cada grupo = o nó que produz os valores (ícone/cor/nome/tag do catálogo). Os
  // campos (conhecidos com exemplo + dinâmicos da última execução) ficam como
  // FOLHAS planas dentro do nó. Lista única, em ordem de FLUXO. Ver handoff.
  const getByPath = (obj, path) => String(path || "").split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
  const varGroups = (() => {
    const stepMap = stepNamesFor(elements);
    // ordem de FLUXO (gatilho → próximos nós) via BFS pelas arestas
    const order = {};
    let oi = 0;
    const adjOut = {};
    elements.forEach(e => { if (e.source && e.target) (adjOut[e.source] = adjOut[e.source] || []).push(e.target); });
    const trigEl = elements.find(isTriggerNode);
    const oq = trigEl ? [trigEl.id] : [];
    const oseen = new Set();
    while (oq.length) {
      const id = oq.shift();
      if (oseen.has(id)) continue;
      oseen.add(id);
      order[id] = oi++;
      (adjOut[id] || []).forEach(t => oq.push(t));
    }
    const orderOf = id => (order[id] != null ? order[id] : 9999);
    const groups = [];
    if (trigEl) {
      const tt = trigEl.data && trigEl.data.triggerType;
      const tdef = (tt && TRIGGERS[tt]) || {};
      const fields = (tdef.out && tdef.out.fields) || [];
      groups.push({
        id: "trigger",
        ord: orderOf(trigEl.id),
        title: tdef.title || "Gatilho",
        verbKey: "__trigger__",
        category: "routing",
        tag: tt || "gatilho",
        accent: nodeAccent("__trigger__", "routing"),
        prefix: "steps.trigger",
        known: fields.map(f => ({
          key: f.path,
          token: `steps.trigger.event.body.${f.path}`,
          example: getByPath(triggerSample, `event.body.${f.path}`),
        })),
        value: triggerSample && Object.keys(triggerSample).length ? triggerSample : null,
        dynamic: true,
      });
    }
    elements.forEach(n => {
      if (!isNode(n) || isTriggerNode(n)) return;
      const def = n.data && n.data.verbKey && VERBS[n.data.verbKey];
      if (!def) return;
      const hasKnown = def.exports && def.exports.length;
      if (!hasKnown && !def.exportsDynamic) return; // nó não produz variável
      const step = stepMap[n.id];
      const real = lastRunSteps[step] && lastRunSteps[step].output;
      // nós de saída dinâmica (ex.: Executar código) não têm exports estáticos;
      // inferimos os campos do `return {...}` para o picker mostrar sem rodar.
      const known = hasKnown
        ? [
            ...def.exports.map(e => ({
              key: e.key,
              token: `steps.${step}.${e.key}`,
              example: real ? getByPath(real, e.key) : e.sample,
            })),
            // WhatsApp: cada Saída do agente vira result.<campo> (+ o que veio da última execução)
            ...(n.data.verbKey === "transfer_to_ai"
              ? Array.from(
                  new Set([
                    ...agentOutputNames(n),
                    ...(real && real.result && typeof real.result === "object" ? Object.keys(real.result) : []),
                  ])
                ).map(k => ({
                  key: `result.${k}`,
                  token: `steps.${step}.result.${k}`,
                  example: real ? getByPath(real, `result.${k}`) : undefined,
                }))
              : []),
          ]
        : (n.data.verbKey === "transfer_ai"
            ? // Saídas do agente (cacheadas no nó ao escolher o agente) + o que vier
              // da última execução + o resumo/sucesso do apurador
              Array.from(new Set([...agentOutputNames(n), ...(real && typeof real === "object" ? Object.keys(real) : []), "resumo", "sucesso"]))
            : inferReturnKeys(n.data.code)
          ).map(k => ({
            key: k,
            token: `steps.${step}.${k}`,
            example: real ? getByPath(real, k) : undefined,
          }));
      groups.push({
        id: step,
        ord: orderOf(n.id),
        title: def.title,
        verbKey: n.data.verbKey,
        category: def.category,
        tag: def.label,
        accent: nodeAccent(n.data.verbKey, def.category),
        prefix: `steps.${step}`,
        known,
        value: real && typeof real === "object" ? real : null,
        dynamic: !!def.exportsDynamic,
      });
    });
    // ITEM DO LOOP ({{item.*}}): para cada "Repetir para cada item", descobre a
    // FORMA do item resolvendo a lista (ex.: {{steps.http_request.body}}) contra o
    // scope — pega o 1º elemento e lista seus campos, com exemplos da última execução.
    elements.forEach(n => {
      if (!isNode(n) || !n.data || n.data.verbKey !== "loop") return;
      const nm = (n.data.itemAs || "item").trim() || "item";
      let arr = null;
      const m = /^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/.exec(String(n.data.list || ""));
      if (m) {
        arr = getByPath(scope, m[1].trim());
        if (typeof arr === "string") { try { arr = JSON.parse(arr); } catch (e) { arr = null; } }
      }
      const sample = Array.isArray(arr) && arr.length && arr[0] && typeof arr[0] === "object" ? arr[0] : null;
      const known = sample ? Object.keys(sample).map(k => ({ key: k, token: `${nm}.${k}`, example: sample[k] })) : [];
      groups.push({
        id: `loopitem:${n.id}`,
        ord: orderOf(n.id) + 0.5, // logo após o cabeçalho do loop
        title: `Item do loop (${nm})`,
        verbKey: "loop",
        category: "advanced",
        tag: nm,
        accent: nodeAccent("loop", "advanced"),
        prefix: nm,
        known,
        value: sample,
        dynamic: true,
      });
    });
    return groups.sort((a, b) => a.ord - b.ord);
  })();

  // app still unnamed -> needs the naming modal before save/publish
  const needsNaming = () => {
    const n = (name || "").trim();
    return n === "" || n.toLowerCase() === "sem título";
  };

  // SUGESTÃO DE NOME (IA, em BACKGROUND): quando o fluxo tem conteúdo e ainda não
  // foi nomeado, monta um resumo do fluxo INTEIRO (não só o gatilho) e pede um nome
  // curto à IA — antes de o usuário salvar, pra não atrasar o modal. Debounced;
  // só refaz se o resumo mudou. (Fluxos montados pelo copiloto já vêm nomeados via
  // set_name → needsNaming() é false e nem entra aqui.)
  const flowSummary = () => {
    const trig = elements.find(isTriggerNode);
    const tt = trig && trig.data && trig.data.triggerType;
    const tTitle = (tt && TRIGGERS[tt] && TRIGGERS[tt].title) || tt || "";
    const lines = [];
    if (tTitle) lines.push(`Gatilho: ${tTitle}`);
    elements.filter(el => isNode(el) && !isTriggerNode(el)).forEach(n => {
      const def = n.data && n.data.verbKey && VERBS[n.data.verbKey];
      if (!def) return;
      let extra = "";
      try { extra = def.summary ? String(def.summary(n.data) || "").slice(0, 60) : ""; } catch (e) { extra = ""; }
      lines.push(`- ${def.title || n.data.verbKey}${extra ? `: ${extra}` : ""}`);
    });
    return lines.join("\n");
  };

  useEffect(() => {
    if (!needsNaming()) return undefined;
    const hasBody = elements.some(el => isNode(el) && !isTriggerNode(el));
    if (!hasBody) return undefined;
    const summary = flowSummary();
    if (!summary || summary === suggestHashRef.current) return undefined;
    const t = setTimeout(async () => {
      suggestHashRef.current = summary;
      try {
        const { data } = await api.post("/ai/suggest-name", { summary });
        if (data && data.name) setSuggestedName(data.name);
      } catch (e) { /* silencioso — segue sem sugestão */ }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, name]);

  // ao abrir o modal de nomear, pré-preenche com a sugestão (se houver e o campo
  // estiver vazio) — o usuário pode editar/limpar.
  useEffect(() => {
    if (nameModal && suggestedName && needsNaming()) setName(suggestedName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameModal]);

  // call-context guard: call nodes (transferir, áudio, coletar, URA, fila) need a
  // call — an inbound call trigger or an "Iniciar chamada" node in the flow.
  const triggerNode = elements.find(isTriggerNode);
  const triggerType = triggerNode && triggerNode.data && triggerNode.data.triggerType;
  const hasOriginate = elements.some(el => isNode(el) && el.data && el.data.verbKey === "originate");
  const dir = triggerType ? triggerDirection(triggerType) : "inbound";
  const hasCall = dir === "inbound" || dir === "both" || hasOriginate || triggerProvidesCall(triggerType);
  // modo Ferramenta: canvas só de backend (sem nós de chamada) + nó Retornar.
  // vale tanto pra app nova (queryKind) quanto pra carregada (triggerType "tool").
  const toolMode = triggerType === "tool" || (isNew && queryKind === "tool");
  // modo Coletor: mesmo racional do toolMode — canvas só de backend + nó
  // "Retornar ao coletor". O nó do gatilho é o "Polling" (frequência).
  const collectorMode = triggerType === "collector" || queryKind === "collector";
  // fluxo vazio = o usuário quer montar → o copiloto começa direto no especialista
  // de automação (pula a triagem: uma chamada e ~5s a menos — STU-160)
  const copilotHasNodes = elements.some(el => isNode(el) && !isTriggerNode(el));
  const copilotStartAgent = !toolMode && !collectorMode && !copilotHasNodes ? "automation" : undefined;
  // copiloto trabalhando num fluxo ainda VAZIO → mostra o nó provisório
  const copilotBuilding = copilotBusy && !toolMode && !collectorMode && !copilotHasNodes;
  // o copiloto começou este fluxo do zero → é "dele" nesta sessão (STICKY)
  const copilotMadeFlowRef = useRef(false);
  if (copilotBuilding) copilotMadeFlowRef.current = true;
  const copilotFlowGuard = useMemo(
    () => ({ owned: aiEditable || copilotMadeFlowRef.current || !copilotHasNodes, name: name && name !== "Sem Título" ? name : "" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aiEditable, copilotHasNodes, name, copilotBuilding]
  );
  // ao aparecer, ele conta como o 1º nó: o layout "hero" inverte (chat encolhe,
  // canvas cresce, igual ao 1º nó real) e, terminada a transição de largura, o
  // canvas dá zoom e centraliza nele — o usuário vê onde a IA vai montar
  useEffect(() => {
    if (!copilotBuilding) return undefined;
    setBuilt(true);
    const t = setTimeout(() => {
      try {
        const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const trig = elementsRef.current.find(el => isTriggerNode(el));
        const zph = zphRef.current;
        if (!trig || !zph || !zph.setCenter) return;
        // no provisório (o card vazio do gatilho fica escondido), num zoom de leitura
        zph.setCenter(trig.position.x + 145, trig.position.y + 150 + 42, 1, reduce ? 0 : 600);
      } catch (e) {
        /* ignore */
      }
    }, 650);
    return () => clearTimeout(t);
  }, [copilotBuilding]);
  // guia "Primeiros passos": pendências auto-detectadas (campos obrigatórios
  // vazios + marcadores <...>) + o setup autorado do template.
  // liga/desliga o tour guiado "Primeiros passos" (SetupTour). false = desativado.
  const SETUP_TOUR_ENABLED = false;
  const pending = detectPending(elements);
  const hasGuide = SETUP_TOUR_ENABLED && (!!setup || pending.length > 0);
  // Bloco de repetição (container Modelo B): pareia os marcadores loop/loop_end
  // (por posição vertical, ciente de aninhamento via pilha) e DERIVA um nó-frame
  // tintado por par, desenhado ATRÁS dos passos (z menor = primeiro na lista).
  // Não toca os elements salvos (compile/save/validação seguem intactos).
  const innerIds = loopInnerIds(elements);
  const loopFrames = (() => {
    // frame FLUSH: começa no topo do cabeçalho (sem offset) e vai até o rodapé.
    // O cabeçalho (loop) e o rodapé (loop_end) são barras de largura cheia que
    // encostam nas bordas do frame — viram o topo/base do container.
    const FRAME_BOTTOM = 44; // altura do rodapé (p/ o frame fechar embaixo dele)
    const markers = elements
      .filter(el => isNode(el) && el.data && (el.data.verbKey === "loop" || el.data.verbKey === "loop_end") && el.position)
      .sort((a, b) => a.position.y - b.position.y);
    const drag = loopDragRef.current; // arraste do cabeçalho em curso (se houver)
    const stack = [];
    const frames = [];
    markers.forEach(m => {
      if (m.data.verbKey === "loop") { stack.push(m); return; }
      const s = stack.pop();
      if (!s) return;
      // durante o arraste do cabeçalho, o `elements` do loop fica defasado — usa o
      // delta acumulado p/ o frame seguir o cabeçalho VIVO (o loop_end já é membro,
      // então já se moveu; só o topo precisa do offset).
      const d = drag && drag.id === s.id ? drag.total : null;
      const x = s.position.x + (d ? d.x : 0);
      const yTop = s.position.y + (d ? d.y : 0);
      const yBot = m.position.y + FRAME_BOTTOM;
      frames.push({
        id: `loopframe:${s.id}`,
        type: "loopFrame",
        position: { x, y: yTop },
        data: { w: LOOP_FRAME_W, h: Math.max(80, yBot - yTop) },
        draggable: false,
        selectable: false,
        connectable: false,
      });
    });
    return frames;
  })();
  // passos internos: deslocados à direita (só no display) p/ parecerem dentro.
  // Dentro de um bloco, SÓ o cabeçalho (loop) é arrastável — o rodapé (loop_end)
  // e os passos internos ficam travados (movem junto via o cabeçalho).
  const hasLoopBlock = loopFrames.length > 0;
  const shownElements = hasLoopBlock
    ? elements.map(el => {
        if (!isNode(el)) return el;
        const vk = el.data && el.data.verbKey;
        if (innerIds.has(el.id)) {
          return { ...el, draggable: false, position: { ...el.position, x: el.position.x + LOOP_INDENT } };
        }
        if (vk === "loop_end") return { ...el, draggable: false };
        if (vk === "loop") {
          // bloco vazio? (loopBlockIds devolve [..internos, loop_end]) → mostra o
          // botão "Adicionar passo dentro" no cabeçalho (senão não há como add).
          const empty = loopBlockIds(elements, el.id).length <= 1;
          return { ...el, data: { ...el.data, _empty: empty } };
        }
        return el;
      })
    : elements;
  // nós cujo OUTPUT da última execução tem uma LISTA (array) → ganham o ícone
  // "Repetir para cada item desta lista" no card. Guarda o token da lista (ex.:
  // {{steps.http_request.json}}) p/ pré-preencher o loop. `body` (texto JSON) conta.
  const listTokenByNode = (() => {
    const stepMap2 = stepNamesFor(elements);
    const map = {};
    elements.forEach(n => {
      if (!isNode(n) || isTriggerNode(n)) return;
      const step = stepMap2[n.id];
      const out = lastRunSteps[step] && lastRunSteps[step].output;
      if (!out || typeof out !== "object") return;
      let field = Object.keys(out).find(k => Array.isArray(out[k]));
      if (!field) field = Object.keys(out).find(k => {
        if (typeof out[k] !== "string") return false;
        try { return Array.isArray(JSON.parse(out[k])); } catch (e) { return false; }
      });
      if (field) map[n.id] = `{{steps.${step}.${field}}}`;
    });
    // GATILHO: também pode trazer uma lista (ex.: webhook cujo body é/contém um
    // array). Busca o 1º array dentro do triggerSample e aponta o token completo.
    const trigEl = elements.find(isTriggerNode);
    if (trigEl && triggerSample && typeof triggerSample === "object") {
      const findArr = (obj, prefix, depth) => {
        if (depth > 4 || obj == null) return null;
        if (Array.isArray(obj)) return { path: prefix, arr: obj };
        if (typeof obj === "string") { try { const pj = JSON.parse(obj); if (Array.isArray(pj)) return { path: prefix, arr: pj }; } catch (e) {} return null; }
        if (typeof obj !== "object") return null;
        for (const k of Object.keys(obj)) if (Array.isArray(obj[k])) return { path: prefix ? `${prefix}.${k}` : k, arr: obj[k] };
        for (const k of Object.keys(obj)) { const r = findArr(obj[k], prefix ? `${prefix}.${k}` : k, depth + 1); if (r) return r; }
        return null;
      };
      const found = findArr(triggerSample, "", 0);
      if (found && found.arr.length && found.arr[0] && typeof found.arr[0] === "object") {
        map[trigEl.id] = found.path ? `{{steps.trigger.${found.path}}}` : "{{steps.trigger}}";
      }
    }
    return map;
  })();
  // nó provisório "Criando automação…" enquanto o copiloto monta um fluxo vazio
  // (STU-160): só na tela — não entra em `elements` (nem salva, nem a IA vê)
  const phTrigger = copilotBuilding ? elements.find(el => isTriggerNode(el)) : null;
  const placeholderEls = phTrigger
    ? [
        {
          id: PLACEHOLDER_ID,
          type: "placeholderNode",
          position: { x: phTrigger.position.x, y: phTrigger.position.y + 150 },
          data: { label: "Criando automação", status: copilotStatus },
          draggable: false,
          selectable: false,
          connectable: false,
        },
        { id: `${PLACEHOLDER_ID}_edge`, source: phTrigger.id, target: PLACEHOLDER_ID, animated: true, type: "smoothstep", style: { strokeDasharray: "4 4" } },
      ]
    : [];
  const displayElements = (loopFrames.length ? [...loopFrames, ...shownElements] : shownElements)
    .map(el => (isNode(el) && listTokenByNode[el.id] ? { ...el, data: { ...el.data, _listToken: listTokenByNode[el.id] } } : el))
    .concat(placeholderEls);
  // sequência do tour: passos AUTORADOS do template (cada um ancorado a um nó via
  // `node` = step-name) + pendências auto-detectadas de nós não cobertos. Assim o
  // tour caminha por TODOS os passos, mesmo os que não têm pendência detectável.
  const tourSteps = (() => {
    const stepMap = stepNamesFor(elements); // nodeId -> step-name (verbKey)
    const nodeByStep = {};
    Object.keys(stepMap).forEach(nid => { nodeByStep[stepMap[nid]] = nid; });
    const nodeTitle = nid => {
      const el = elements.find(e => e.id === nid);
      const vk = el && el.data && el.data.verbKey;
      return (vk && VERBS[vk] && VERBS[vk].title) || "nó";
    };
    const authored = (((setup && setup.steps) || [])).map(st => {
      const nodeId = st.node ? nodeByStep[st.node] || null : null;
      const tracks = nodeId ? pending.filter(p => p.nodeId === nodeId).map(p => p.field) : [];
      return {
        kind: "todo",
        nodeId,
        title: st.title,
        description: st.description,
        node: nodeId ? nodeTitle(nodeId) : null,
        tracks,
      };
    });
    const covered = new Set(authored.map(a => a.nodeId).filter(Boolean));
    const extra = pending
      .filter(p => !covered.has(p.nodeId))
      .map(p => ({
        kind: "todo",
        nodeId: p.nodeId,
        title: p.field ? `Configure "${p.field}"` : p.message,
        description: `No nó ${p.node}, preencha o campo destacado.`,
        node: p.node,
        tracks: [p.field],
      }));
    return [...authored, ...extra];
  })();
  // abre o guia uma vez por app (ex.: template recém-clonado com pendências)
  const guideSeenRef = useRef(false);
  useEffect(() => {
    if (!SETUP_TOUR_ENABLED) return;
    if (isNew || guideSeenRef.current || !applicationId) return;
    if (!setup && pending.length === 0) return;
    const key = `numera:setupseen:${applicationId}`;
    let seen = false;
    try { seen = localStorage.getItem(key) === "1"; } catch (e) { /* */ }
    guideSeenRef.current = true;
    if (!seen) {
      setSetupOpen(true);
      try { localStorage.setItem(key, "1"); } catch (e) { /* */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, setup, pending.length, isNew]);
  // pra onde o botão Voltar leva: Ferramenta → lista de Ferramentas; Agente →
  // hub de Agentes; demais → lista de Apps.
  const backTo =
    toolMode || triggerType === "tool"
      ? "/agents/tools"
      : collectorMode || triggerType === "collector"
      ? "/collectors"
      : triggerType === "agent" || queryKind === "agent"
      ? "/agents"
      : "/applications";
  // contexto para o sistema de validação de fluxo (flowValidation.js). Monta a
  // lista de nós + o grafo a partir dos elementos do react-flow, sem acoplar as
  // regras ao react-flow. Ver flowValidation.js para a lista de regras.
  const buildFlowCtx = (els = elements) => {
    const trig = els.find(isTriggerNode);
    const nodes = [];
    const verbById = {};
    const adj = {};
    els.forEach(el => {
      if (isNode(el)) {
        const verb = el.data && el.data.verbKey;
        verbById[el.id] = verb;
        if (!isTriggerNode(el)) nodes.push({ id: el.id, verb, data: el.data || {} });
      }
      if (el.source && el.target) (adj[el.source] = adj[el.source] || []).push(el.target);
    });
    if (trig) verbById[trig.id] = trig.data && trig.data.verbKey;
    // handlers compilados (arrays lineares — o que o motor executa) + mapa
    // _name->nodeId, para regras estruturais como o balanceamento de loop.
    let handlers = null;
    try {
      handlers = compile(els, { name, description }).definition.handlers || null;
    } catch (e) {
      handlers = null;
    }
    const sn = stepNamesFor(els);
    const stepNameToId = {};
    Object.keys(sn).forEach(id => { stepNameToId[sn[id]] = id; });
    return {
      nodes,
      triggerType,
      triggerData: (trig && trig.data) || {}, // config do gatilho (ex.: inboundNumberId)
      hasCall,
      handlers,
      stepNameToId,
      inputParams: (trig && trig.data && trig.data.inputParams) || [], // params declarados da ferramenta
      graph: { triggerId: trig && trig.id, verbById, adj },
    };
  };

  // mantém o validador apontando para o estado atual (para a tool validate_flow)
  validateRef.current = () => flowErrors(buildFlowCtx());

  const doSave = async () => {
    try {
      await saveDraft();
      toast.success(i18n.t("apps.saved"));
    } catch (err) {
      toastError(err);
    }
  };
  const doPublish = async (force = false) => {
    try {
      const id = await saveDraft();
      const { data } = await api.post(`/applications/${id}/publish`, force ? { force: true } : {});
      setDirty(false);
      setPublishedVersion(data.version.version);
      toast.success(i18n.t("apps.published", { v: data.version.version }));
      // após publicar, enquadra o fluxo (não dá zoom só no gatilho): mostra TODOS
      // os nós; com poucos, limita o zoom pra caber ~4 componentes com folga.
      setTimeout(() => {
        const rf = rfInstanceRef.current;
        if (rf) try { rf.fitView({ padding: 0.2, maxZoom: PUBLISH_ZOOM }); } catch (e) { /* ignore */ }
      }, 120);
      return true;
    } catch (err) {
      // 409 = the inbound number is in use by another app; ask to confirm the move
      const resp = err && err.response;
      if (resp && resp.status === 409 && !force) {
        const move = await confirm({
          title: "Número já em uso",
          body: (resp.data && resp.data.error) || "Este número está em uso por outro fluxo. Publicar aqui vai movê-lo para este fluxo.",
          variant: "destructive",
          confirmText: "Publicar e mover o número",
        });
        if (move) return doPublish(true);
        return false;
      }
      // guarda o motivo REAL: o copiloto só recebia "falha ao publicar" e, sem
      // saber que era limite de plano, ficava editando o fluxo e tentando de novo.
      publishErrRef.current = {
        status: (resp && resp.status) || 0,
        message: (resp && resp.data && resp.data.error) || err.message || "",
      };
      toastError(err);
      return false;
    }
  };

  // "Publicar e sair": publica e, se concluir limpo (sem validação/naming/conflito
  // pendentes), navega. Nas guardas, o fluxo de publicar assume e o usuário fica.
  // FERRAMENTA: o valor que a IA recebe sai do nó "Retornar". Em vez de barrar a
  // publicação pedindo esse nó, ligamos um "Retornar" sozinho no fim de cada
  // caminho sem saída, devolvendo a saída do último nó (o campo útil quando há
  // um — json do HTTP, rows do MySQL, text do Prompt…). Fica visível e editável
  // no canvas. Se já existe algum "Retornar", não mexe.
  const RETURN_FIELD = { http_request: "json", mysql_query: "rows", ai_prompt: "text", data_get: "value" };
  const withAutoToolReturn = () => {
    const els = elementsRef.current;
    if (!toolMode) return els;
    if (els.some(el => isNode(el) && el.data && el.data.verbKey === "return")) return els;
    const withOut = new Set(els.filter(el => el.source && el.target).map(el => el.source));
    const leaves = els.filter(
      el => isNode(el) && !isTriggerNode(el) && !withOut.has(el.id) && el.data && el.data.verbKey !== "loop_end"
    );
    if (!leaves.length) return els;
    const sn = stepNamesFor(els);
    const add = [];
    leaves.forEach(leaf => {
      const id = `n_${idRef.current++}`;
      const field = RETURN_FIELD[leaf.data.verbKey];
      const pos = leaf.position || { x: 260, y: 20 };
      add.push({
        id,
        type: "verbNode",
        position: { x: pos.x, y: pos.y + 150 },
        data: { verbKey: "return", value: `{{steps.${sn[leaf.id]}${field ? `.${field}` : ""}}}`, description: "" },
      });
      add.push({ id: `e_${leaf.id}_next_${id}`, source: leaf.id, sourceHandle: "next", target: id, ...edgeOpts });
    });
    const next = els.concat(add);
    elementsRef.current = next; // saveDraft/compile leem o ref — vale já nesta publicação
    setElements(next);
    toast.info(leaves.length > 1 ? `Liguei ${leaves.length} nós "Retornar" no fim dos caminhos da ferramenta.` : 'Liguei um nó "Retornar" no fim da ferramenta.');
    return next;
  };

  const publishAndLeave = async to => {
    const errs = flowErrors(buildFlowCtx(withAutoToolReturn()));
    if (errs.length) { setFlowIssues(errs); return; }
    if (needsNaming()) { setNameError(""); setNameModal({ action: "publish" }); return; }
    const ok = await doPublish();
    if (ok && to) history.push(to);
  };

  // templates de WhatsApp ainda não aprovados (rascunho/aguardando/rejeitado) —
  // bloqueiam a publicação (não dá pra enviar um template que não existe/aprovado)
  const pendingTpls = elements.filter(
    el => el.data && el.data.templateDraft && el.data.templateDraft.status !== "APPROVED"
  );
  const tplBlockMsg =
    "Há template(s) de WhatsApp pendentes de criação/aprovação. Crie-os e aguarde a aprovação do WhatsApp antes de publicar.";

  // STU-163: fluxo de "Ao receber mensagem" (WhatsApp) numa conexão que outro fluxo
  // ou um agente já usa → texto do aviso (ou null). Melhor esforço: falhou, publica.
  const whatsappUsageWarning = async () => {
    try {
      const trig = elementsRef.current.find(el => isTriggerNode(el));
      const td = (trig && trig.data) || {};
      if (!/^event\.(whatsapp_qr|gupshup)\.ao_receber_mensagem$/.test(String(td.triggerType || "")) || !td.connectorId) return null;
      const id = appIdRef.current || numericId || 0;
      const { data } = await api.get(`/connectors/${td.connectorId}/usage`, { params: { excludeAppId: id } });
      const flows = (data && data.flows) || [];
      const agent = data && data.agent;
      if (!flows.length && !agent) return null;
      const names = flows.map(f => `"${f.name}"`).join(", ");
      if (td.interceptExclusive) {
        const otherExcl = flows.filter(f => f.exclusive).map(f => `"${f.name}"`).join(", ");
        return [
          `Este fluxo tem captura exclusiva: ${[flows.length ? `o${flows.length > 1 ? "s fluxos" : " fluxo"} ${names}` : "", agent ? `o agente "${agent.name}"` : ""].filter(Boolean).join(" e ")} deixa${flows.length + (agent ? 1 : 0) > 1 ? "m" : ""} de receber as mensagens desta conexão.`,
          otherExcl ? ` ${otherExcl} também tem captura exclusiva — vale o publicado por último, que passa a ser este.` : "",
        ].join("");
      }
      return [
        flows.length ? `As mensagens desta conexão também vão para ${flows.length > 1 ? "os fluxos" : "o fluxo"} ${names}: ${flows.length > 1 ? "todos respondem" : "os dois respondem"} às mesmas mensagens. Para só este responder, ligue "Captura exclusiva" no gatilho.` : "",
        agent ? ` Esta conexão é atendida pelo agente "${agent.name}": este fluxo só recebe mensagens com palavra-chave ou com "Captura exclusiva".` : "",
      ].join("").trim();
    } catch (e) {
      return null;
    }
  };

  const handlePublish = async () => {
    const errs = flowErrors(buildFlowCtx(withAutoToolReturn()));
    if (errs.length) { setFlowIssues(errs); return; }
    if (pendingTpls.length) { setTemplateModalOpen(true); toast.warn(tplBlockMsg); return; }
    if (needsNaming()) { setNameError(""); setNameModal({ action: "publish" }); return; }
    const usageWarn = await whatsappUsageWarning();
    if (usageWarn && !(await confirm({ title: "Esta conexão do WhatsApp já está em uso", message: usageWarn, confirmText: "Publicar assim mesmo" }))) return;
    // DIFF antes de publicar: mostra o que mudou vs a versão no ar e confirma.
    // (1ª publicação — sem versão no ar — não tem o que comparar: publica direto.)
    if (publishedVersion != null) {
      try {
        const id = appIdRef.current || numericId || applicationId;
        const { data: ver } = await api.get(`/applications/${id}/versions/${publishedVersion}`);
        setPublishDiff({ version: publishedVersion, ...diffFlow(Array.isArray(ver.flow) ? ver.flow : [], elementsRef.current) });
        return; // o diálogo confirma → doPublish
      } catch (e) { /* falhou o diff → publica direto */ }
    }
    doPublish();
  };

  // ações do copiloto (publish/dial) — reapontadas a cada render p/ estado atual
  publishRef.current = async () => {
    const errs = flowErrors(buildFlowCtx(withAutoToolReturn()));
    if (errs.length) return { ok: false, issues: errs.map(e => ({ message: e.message, nodeId: e.nodeId || null })) };
    if (pendingTpls.length) return { ok: false, error: tplBlockMsg };
    if (needsNaming()) return { ok: false, needsName: true, error: "Defina um nome (set_name) antes de publicar." };
    publishErrRef.current = null;
    const usageWarn = await whatsappUsageWarning();
    const ok = await doPublish();
    // o copiloto já pediu a confirmação do usuário para publicar; o aviso volta para ele explicar
    if (ok) return usageWarn ? { ok: true, warning: usageWarn, hint: "Explique este aviso ao usuário em 1 frase." } : { ok: true };
    const e = publishErrRef.current || {};
    // 402 = limite de plano. É DETERMINÍSTICO: tentar de novo, ou mexer no fluxo,
    // não muda nada — só queima token. `retryable: false` corta o laço do copiloto.
    if (e.status === 402) {
      return {
        ok: false,
        retryable: false,
        error: e.message || "Limite do plano atingido.",
        hint: "Não tente publicar de novo nem altere o fluxo: é limite do plano do usuário. Explique e pare.",
      };
    }
    return { ok: false, error: e.message || "Falha ao publicar (veja os avisos na tela)." };
  };
  dialRef.current = async ({ to, from } = {}) => {
    const id = appIdRef.current || numericId;
    if (!id) return { ok: false, error: "Salve/publique o fluxo antes de discar." };
    try {
      const { data } = await api.post(`/applications/${id}/dial`, { to, from });
      return { ok: true, sid: data.sid, to: data.to };
    } catch (e) {
      return { ok: false, error: (e.response && e.response.data && e.response.data.error) || e.message };
    }
  };

  // naming modal: validate name (non-empty, not "Sem Título", not duplicated within
  // the project) then run the pending save/publish. For an existing app, also apply
  // the chosen folder.
  const confirmName = async () => {
    const n = (name || "").trim();
    if (n.length < 2) { setNameError("Dê um nome com pelo menos 2 caracteres."); return; }
    if (n.toLowerCase() === "sem título") { setNameError("Escolha um nome diferente de “Sem Título”."); return; }
    const dup = allApps.some(
      a => a.name && a.name.trim().toLowerCase() === n.toLowerCase() && String(a.id) !== String(applicationId)
    );
    if (dup) { setNameError("Já existe uma automação com esse nome neste projeto."); return; }
    const action = nameModal.action;
    setNameModal(null);
    if (!isNew) {
      try { await api.put(`/applications/${applicationId}/folder`, { folderId }); } catch (e) { /* ignore */ }
    }
    if (action === "save") await doSave();
    else await doPublish();
  };

  const openVersions = async () => {
    if (isNew) {
      toast.info(i18n.t("apps.saveFirst"));
      return;
    }
    try {
      const { data } = await api.get(`/applications/${applicationId}/versions`);
      setVersions(data.versions);
      setVersionsOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  // organiza os nós em árvore (botão "Organizar" + auto após o copiloto montar)
  const organizeLayout = () => {
    setElements(els => autoLayoutElements(els));
    setDirty(true);
    if (rfInstance) setTimeout(() => { try { rfInstance.fitView({ padding: 0.2, maxZoom: INITIAL_ZOOM }); } catch (e) { /* ignore */ } }, 60);
  };

  const handleRestore = async v => {
    try {
      const { data } = await api.post(
        `/applications/${applicationId}/versions/${v}/restore`,
        {}
      );
      if (Array.isArray(data.flow)) {
        setElements(data.flow);
        reseedIds(data.flow);
      }
      setName(data.name);
      setDirty(true);
      setSelectedId(null);
      setVersionsOpen(false);
      toast.success(i18n.t("apps.restored", { v }));
    } catch (err) {
      toastError(err);
    }
  };

  // ---- copilot tool executor (maps Claude/OpenAI tool calls -> canvas edits) ----
  // `params` das tools add_node/update_node: o modelo às vezes manda o objeto
  // SERIALIZADO ("{\"code\":\"...\"}") em vez de objeto — típico quando o valor é
  // código com quebras de linha. Espalhar uma string com {...str} gerava
  // {0:"c",1:"o",...}: o nó ficava cheio de caracteres numerados e o campo real
  // (ex.: code) permanecia no valor ANTIGO, porque a chave nunca chegava.
  // Aqui normalizamos: string vira objeto por JSON.parse; o que não for objeto
  // simples é recusado com erro claro em vez de virar lixo silencioso.
  const asParams = raw => {
    if (raw == null) return {};
    let v = raw;
    if (typeof v === "string") {
      const t = v.trim();
      if (!t) return {};
      try {
        v = JSON.parse(t);
      } catch (e) {
        return { __error: "params veio como texto e não é JSON válido; mande um objeto." };
      }
    }
    if (typeof v !== "object" || Array.isArray(v)) {
      return { __error: "params deve ser um objeto { campo: valor }." };
    }
    return v;
  };

  // aplica uma mudança do copiloto nos elementos JÁ (não só no próximo render):
  // com várias ações na mesma resposta (STU-160), o add_node seguinte precisa
  // enxergar o nó criado pelo anterior para se ligar a ele
  const commitEls = fn => {
    const next = fn(elementsRef.current);
    elementsRef.current = next;
    setElements(next);
  };
  const runCopilotTool = useCallback((toolName, rawInput = {}) => {
    const els = elementsRef.current;
    // "ref:<apelido>" → id real do nó criado por um add_node com esse ref (mesma
    // resposta ou anterior). Permite encadear vários nós numa resposta só.
    const R = v => (typeof v === "string" && v.startsWith("ref:") ? copilotRefsRef.current[v.slice(4)] || v : v);
    const input = { ...rawInput };
    ["after_id", "source_id", "target_id", "node_id"].forEach(k => {
      if (input[k] !== undefined) input[k] = R(input[k]);
    });
    try {
      // HANDOFF entre agentes (mesmo chat): transfere o "cérebro" ativo via __extra.
      if (toolName === "transfer_to_automation") {
        return { __extra: { activeAgent: "automation", mission: input.mission }, ok: true, note: "Transferido para o especialista de automações." };
      }
      if (toolName === "transfer_to_collector") {
        return { __extra: { activeAgent: "collector", mission: input.mission }, ok: true, note: "Transferido para o especialista de coletores." };
      }
      if (toolName === "transfer_to_agents") {
        return { __extra: { activeAgent: "agents", mission: input.mission }, ok: true, note: "Transferido para o especialista de agentes de IA." };
      }
      if (toolName === "transfer_back") {
        return {
          __extra: {
            activeAgent: "automation",
            lastCollector: input.collectorId ? { id: input.collectorId, name: input.collectorName } : null,
            lastAgent: input.agentId ? { id: input.agentId, name: input.agentName } : null,
            summary: input.summary,
          },
          ok: true,
        };
      }
      if (toolName === "transfer_to_data") {
        return { __extra: { activeAgent: "data", mission: input.mission }, ok: true, note: "Transferido para o especialista de dados e relatórios." };
      }
      // ferramentas do agente de DADOS & RELATÓRIOS
      if (toolName === "create_dataset") {
        return api
          .post("/datasets", { projectId: projectIdRef.current, name: input.name, fields: input.fields || [] })
          .then(({ data }) => ({ ok: true, datasetId: data.dataset.id, name: data.dataset.name, slug: data.dataset.slug }))
          .catch(e => ({ error: (e.response && e.response.data && e.response.data.error) || e.message }));
      }
      if (toolName === "query_dataset") {
        return api
          .post(`/datasets/${input.datasetId}/summary`, input.spec || {})
          .then(({ data }) => data)
          .catch(e => ({ error: (e.response && e.response.data && e.response.data.error) || e.message }));
      }
      if (toolName === "create_report") {
        return api
          .post("/reports", { datasetId: input.datasetId, name: input.name, spec: input.spec || {}, createdBy: "copiloto" })
          .then(({ data }) => ({ ok: true, reportId: data.report.id, name: data.report.name }))
          .catch(e => ({ error: (e.response && e.response.data && e.response.data.error) || e.message }));
      }
      // memória de longo prazo: a IA grava um fato durável da conta
      if (toolName === "remember") {
        return api.post("/ai/memory", { fact: input.fact }).then(() => ({ ok: true })).catch(e => ({ error: (e.response && e.response.data && e.response.data.error) || e.message }));
      }
      // DIAGNÓSTICO: lê as execuções recentes (histórico de runs) p/ o copiloto
      // ajudar a resolver problemas. Reusa os endpoints já escopados por dono.
      if (toolName === "read_recent_executions") {
        // SEGREDO: mascara valores de chaves sensíveis (x-webhook-secret, authorization,
        // token, senha, api-key, cookie…) ANTES de ir pro copiloto — senão o segredo
        // aparece no chat E fica salvo no histórico da conversa (o LLM recebe o valor).
        const SENSIVEL = /(secret|token|authorization|password|senha|api[-_]?key|cookie|bearer)/i;
        const redact = v => {
          if (Array.isArray(v)) return v.map(redact);
          if (v && typeof v === "object") {
            const o = {};
            for (const k of Object.keys(v)) o[k] = SENSIVEL.test(k) ? "•••" : redact(v[k]);
            return o;
          }
          return v;
        };
        // limita o tamanho (mascarado ANTES de truncar, senão o segredo vazaria na amostra)
        const trim = obj => {
          if (obj == null) return obj;
          try {
            const s = JSON.stringify(obj);
            if (s.length <= 4000) return obj;
            return { _truncado: `${s.length} chars — grande demais p/ exibir inteiro`, _amostra: s.slice(0, 3500) };
          } catch (err) { return obj; }
        };
        const safe = obj => trim(redact(obj));
        // horário SEMPRE em fuso LOCAL legível — o copiloto mostrava o ISO cru (UTC)
        const quando = t => { try { return new Date(t).toLocaleString("pt-BR"); } catch (e) { return t; } };
        // drill-down: cadeia de UMA execução COM os valores (mascarados) de cada passo
        if (input.runId) {
          return api
            .get("/events", { params: { runId: input.runId } })
            .then(({ data }) => ({
              runId: input.runId,
              events: (data.events || []).map(e => ({
                type: e.type,
                status: e.status,
                message: e.message,
                node: (e.data && (e.data.node || e.data.step)) || null,
                step: (e.data && e.data.step) || null,
                input: safe(e.data && e.data.input),
                output: safe(e.data && e.data.output),
                quando: quando(e.createdAt),
              })),
            }))
            .catch(e => ({ error: (e.response && e.response.data && e.response.data.error) || e.message }));
        }
        // lista das execuções recentes (mais novas primeiro). Escopo: fluxo atual
        // por padrão (appMetaRef.current.id = id numérico da app); all_flows = toda a conta.
        const appNumId = (appMetaRef.current || {}).id;
        const params = !input.all_flows && appNumId ? { applicationId: appNumId } : {};
        const limit = Math.min(Number(input.limit) || 10, 20);
        return api
          .get("/events/runs", { params })
          .then(({ data }) => ({
            runs: (data.runs || []).slice(0, limit).map(r => ({
              runId: r.runId,
              status: r.status,
              quando: quando(r.startedAt),
              count: r.count,
              label: r.label,
              trigger: r.trigger,
            })),
          }))
          .catch(e => ({ error: (e.response && e.response.data && e.response.data.error) || e.message }));
      }
      // ferramentas do agente de COLETOR: delega pro backend (introspecção/amostra/
      // memória/criar/testar/publicar/resetar). projectId via ref (deps [] do callback).
      if (["introspect_schema", "run_sample_query", "save_schema_note", "create_collector", "test_collector", "publish_collector", "reset_collector"].includes(toolName)) {
        // no canvas, test/publish/reset operam sobre ESTE coletor → injeta o id do
        // app atual quando o modelo não o passa (na criação, o id vem do create_collector).
        const collInput = { ...input };
        if (!collInput.collectorId && appMetaRef.current && appMetaRef.current.id && ["test_collector", "publish_collector", "reset_collector"].includes(toolName)) {
          collInput.collectorId = appMetaRef.current.id;
        }
        return makeCollectorRunTool(projectIdRef.current, d => { try { toast.info(`Coletor "${d.name}" criado.`); } catch (e) { /* ignore */ } })(toolName, collInput);
      }
      if (toolName === "read_flow") {
        const trig = els.find(isTriggerNode);
        const sn = stepNamesFor(els);
        const nodes = els
          .filter(el => isNode(el) && !isTriggerNode(el))
          .map(n => {
            const { verbKey, ...params } = n.data || {};
            const vdef = VERBS[verbKey] || {};
            // 'step' é o NOME da variável deste nó (use {{steps.<step>.<chave>}});
            // 'produces' são as chaves que ele expõe. NUNCA referencie pelo id.
            const produces = Array.isArray(vdef.exports)
              ? vdef.exports.map(e => e.key)
              : vdef.exportsDynamic
              ? ["(dinâmico: campos definidos pela saída do nó)"]
              : [];
            return { id: n.id, verb: verbKey, step: sn[n.id], title: vdef.title, params, produces };
          });
        const edges = els
          .filter(el => !isNode(el))
          .map(e => ({ source: e.source, source_handle: e.sourceHandle || "next", target: e.target }));
        let triggerConfig = null;
        if (trig && trig.data) {
          const { verbKey: _vk, triggerType: _tt, sample: _s, ...cfg } = trig.data;
          if (Object.keys(cfg).length) triggerConfig = cfg;
        }
        const triggerType = (trig && trig.data && trig.data.triggerType) || null;

        // meta do app + COMO chamá-lo (endpoint por tipo de gatilho). É o que
        // permite ao copiloto dar um curl, o número de entrada, etc. Segredos
        // (secret do webhook, API key) NÃO são expostos aqui — só indicados.
        const meta = appMetaRef.current || {};
        const origin = window.location.origin;
        let endpoint = null;
        if (triggerType === "webhook") {
          endpoint = {
            type: "webhook",
            method: (triggerConfig && triggerConfig.method) || "POST",
            url: meta.webhookId ? `${origin}/w/${meta.webhookId}` : null,
            note: meta.webhookId
              ? "endpoint público. Se houver 'secret' configurado no gatilho, precisa ir no header combinado — o valor do secret NÃO é exposto aqui; o usuário vê na aba do gatilho."
              : "salve o fluxo uma vez para gerar a URL do webhook.",
          };
        } else if (triggerType === "api_call") {
          endpoint = {
            type: "api",
            method: "POST",
            url: `${origin}/v1/calls`,
            auth: "Bearer <API key da conta> (Desenvolvedores › Chaves de API)",
            note: "origina uma ligação; este fluxo roda quando ela é atendida. Body ex.: { \"to\": \"+55...\", \"from\": \"+55...\" }.",
          };
        } else if (triggerType === "inbound_call") {
          endpoint = {
            type: "phone",
            inbound_number_id: (triggerConfig && triggerConfig.inboundNumberId) || null,
            note: "ligue para o número da conta associado — resolva o id na lista 'Números da conta' do contexto.",
          };
        } else if (triggerType === "scheduled") {
          endpoint = {
            type: "cron",
            crons: (triggerConfig && (triggerConfig.crons || (triggerConfig.cron ? [triggerConfig.cron] : []))) || [],
            note: "roda por agenda; não é chamado externamente.",
          };
        }
        const app = {
          id: meta.id || null,
          name: nameRef.current,
          published: !!meta.published,
          base_url: origin,
          endpoint,
        };
        return { app, trigger: triggerType, trigger_config: triggerConfig, nodes, edges };
      }
      if (toolName === "set_notes") {
        // notas são documentação: salvam na hora e NÃO marcam o fluxo como
        // alterado (não mudam o que roda). `runCopilotTool` é síncrono — quem
        // chama dá await no retorno, então devolvemos a Promise em vez de usar
        // await aqui dentro.
        const texto = String(input.notes || "");
        setNotes(texto);
        if (!appIdRef.current) return { ok: true, saved: false };
        return api
          .put(`/applications/${appIdRef.current}`, { notes: texto })
          .then(() => ({ ok: true, saved: true }))
          .catch(() => ({ ok: false, error: "não consegui salvar as notas" }));
      }
      if (toolName === "set_trigger") {
        const t = input.trigger_type;
        // config: campos do gatilho (ex.: inboundNumberId p/ inbound_call). Mescla
        // no nó de gatilho, do mesmo jeito que o Picker grava (set_trigger sem
        // config preserva o comportamento antigo: zera a config).
        const cfg = input.config && typeof input.config === "object" ? input.config : {};
        // sample: exemplo do corpo do webhook/evento (a IA já sabe os campos).
        // Vira data.sample = { event: { body } } → alimenta o "Enviar teste" e o
        // picker de variáveis (steps.trigger.event.body.*).
        const sample =
          input.sample && typeof input.sample === "object" ? { event: { body: input.sample } } : undefined;
        commitEls(es =>
          es.map(el =>
            isTriggerNode(el)
              ? { ...el, data: { verbKey: "__trigger__", triggerType: t, ...cfg, ...(sample ? { sample } : {}) } }
              : el
          )
        );
        setDirty(true);
        return { ok: true, trigger: t, config: cfg, sample: !!sample };
      }
      if (toolName === "add_node") {
        const verb = input.verb;
        const def = VERBS[verb];
        if (!def) return { error: `verbo desconhecido: ${verb}` };
        const afterId = input.after_id || "start";
        if (String(afterId).startsWith("ref:")) return { error: `ref desconhecido: ${afterId} — crie o nó com esse ref antes (ou use o id).` };
        const src = els.find(el => el.id === afterId);
        // handle de saída do nó de origem: se ele tem UM único handle nomeado
        // (gather/dial → "action", redirect → "to"), usa esse por padrão. Sem
        // isto, a continuação sai como "next" e o compilador não gera actionHook
        // → o gather no jambonz não tem pra onde mandar o resultado e a chamada
        // morre. O modelo ainda pode forçar via after_handle.
        const srcDef = src && src.data && VERBS[src.data.verbKey];
        const srcHandles = (srcDef && srcDef.handles) || [];
        const onlyHandle = srcHandles.length === 1 ? srcHandles[0].id : null;
        const handle = input.after_handle || (onlyHandle && onlyHandle !== "next" ? onlyHandle : "next");
        const basePos = src && src.position ? src.position : { x: 280, y: 40 };
        const id = `n_${idRef.current++}`;
        if (input.ref) copilotRefsRef.current[String(input.ref)] = id;
        // nome da variável (step) deste nó = verbKey ou verbKey_N (igual a
        // stepNamesFor). Devolvemos pro copiloto referenciar {{steps.<step>...}}.
        const sameVerb = els.filter(el => isNode(el) && !isTriggerNode(el) && el.data && el.data.verbKey === verb).length;
        const step = sameVerb === 0 ? verb : `${verb}_${sameVerb + 1}`;
        // _hl: realce visual transitório "+ criado" (limpo após o turno, ver onAfterRun)
        const addParams = asParams(input.params);
        if (addParams.__error) return { error: addParams.__error };
        const data = { verbKey: verb, ...JSON.parse(JSON.stringify(def.defaults || {})), ...addParams, _hl: true };
        const node = {
          id,
          type: def.isWaMenu ? "waMenuNode" : def.isIvr ? "ivrNode" : def.isBranch ? "branchNode" : "verbNode",
          position: { x: basePos.x, y: basePos.y + 150 },
          data,
        };
        const edge = src
          ? { id: `e_${afterId}_${handle}_${id}`, source: afterId, sourceHandle: handle, target: id, ...edgeOpts, className: "nbEdgeGrow" }
          : null;
        commitEls(es => {
          let next = es;
          if (src) next = next.filter(el => !(el.source === afterId && (el.sourceHandle || "next") === handle));
          return next.concat(edge ? [node, edge] : [node]);
        });
        setDirty(true);
        // auto-pan: se o nó novo nasce fora da área visível, desliza suave até ele
        // (~500ms). É o item mais importante — animação fora da tela não serve.
        // Respeita prefers-reduced-motion (não movimenta o canvas).
        try {
          const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          const zph = zphRef.current;
          const rf = rfInstanceRef.current;
          if (!reduce && zph && zph.setCenter && rf) {
            const t = rf.toObject() || {};
            const zoom = t.zoom || 1;
            const tx = (t.position && t.position[0]) || 0;
            const ty = (t.position && t.position[1]) || 0;
            const wrap = document.querySelector(".react-flow");
            const cw = (wrap && wrap.clientWidth) || 900;
            const ch = (wrap && wrap.clientHeight) || 600;
            const NW = 290, NH = 84; // tamanho aprox do card
            const sx = node.position.x * zoom + tx;
            const sy = node.position.y * zoom + ty;
            const m = 56; // folga
            const off = sx < m || sy < m || sx + NW * zoom > cw - m || sy + NH * zoom > ch - m;
            if (off) zph.setCenter(node.position.x + NW / 2, node.position.y + NH / 2, zoom, 500);
          }
        } catch (e) {
          /* ignore */
        }
        // realce verde some ~1.5s após criar (handoff §1/§4): limpa o _hl deste nó
        setTimeout(() => {
          setElements(es => es.map(el => (el.id === id && el.data && el.data._hl ? { ...el, data: { ...el.data, _hl: false } } : el)));
        }, 1500);
        // remove a classe de "desenhar" da aresta após o draw (evita dasharray fixo)
        if (edge) {
          setTimeout(() => {
            setElements(es => es.map(el => (el.id === edge.id && el.className === "nbEdgeGrow" ? { ...el, className: undefined } : el)));
          }, 800);
        }
        return {
          id,
          ...(input.ref ? { ref: input.ref } : {}),
          verb,
          step,
          title: def.title,
          hint: `Para usar a saída deste nó depois, referencie {{steps.${step}.<campo>}} (use 'step', nunca o id).`,
        };
      }
      if (toolName === "connect") {
        const sh = input.source_handle || "next";
        const eid = `e_${input.source_id}_${sh}_${input.target_id}`;
        commitEls(es =>
          addEdge(
            { id: eid, source: input.source_id, sourceHandle: sh, target: input.target_id, ...edgeOpts, className: "nbEdgeGrow" },
            es.filter(el => !(el.source === input.source_id && (el.sourceHandle || "next") === sh))
          )
        );
        setDirty(true);
        setTimeout(() => {
          setElements(es => es.map(el => (el.id === eid && el.className === "nbEdgeGrow" ? { ...el, className: undefined } : el)));
        }, 800);
        return { ok: true };
      }
      if (toolName === "update_node") {
        const upParams = asParams(input.params);
        if (upParams.__error) return { error: upParams.__error };
        const alvo = els.find(el => el.id === input.node_id);
        if (!alvo) return { error: `nó não encontrado: ${input.node_id}` };
        commitEls(es =>
          es.map(el => (el.id === input.node_id ? { ...el, data: { ...el.data, ...upParams } } : el))
        );
        setDirty(true);
        // devolve o que de fato ficou gravado, para o modelo conferir em vez de
        // confiar num "ok" cego (era assim que a gravação errada passava batida)
        const aplicado = {};
        Object.keys(upParams).forEach(k => { aplicado[k] = upParams[k]; });
        return { ok: true, node_id: input.node_id, applied: Object.keys(aplicado) };
      }
      if (toolName === "delete_node") {
        commitEls(es =>
          es.filter(el => el.id !== input.node_id && el.source !== input.node_id && el.target !== input.node_id)
        );
        setDirty(true);
        return { ok: true };
      }
      if (toolName === "validate_flow") {
        const issues = (validateRef.current() || []).map(e => ({ message: e.message, nodeId: e.nodeId || null }));
        return { ok: issues.length === 0, issues };
      }
      if (toolName === "set_name") {
        const nm = String(input.name || "").trim();
        if (!nm) return { error: "nome vazio" };
        setName(nm);
        setDirty(true);
        return { ok: true, name: nm };
      }
      if (toolName === "publish_flow") {
        return publishRef.current();
      }
      if (toolName === "place_call") {
        if (!input.to) return { ok: false, error: "'to' obrigatório" };
        return dialRef.current({ to: String(input.to), from: input.from ? String(input.from) : undefined });
      }
      if (toolName === "propose_whatsapp_template") {
        const nid = input.node_id;
        const node = els.find(el => el.id === nid);
        if (!node) return { error: `nó não encontrado: ${nid}` };
        const elementName = String(input.element_name || "").toLowerCase().replace(/[^a-z0-9_]/g, "_");
        if (!elementName || !String(input.content || "").trim()) return { error: "element_name e content são obrigatórios" };
        const draft = {
          elementName,
          category: input.category || "UTILITY",
          languageCode: input.language_code || "pt_BR",
          content: String(input.content),
          examples: Array.isArray(input.examples) ? input.examples.map(x => String(x)) : [],
          header: input.header || "",
          headerExample: input.header_example || "",
          footer: input.footer || "",
          buttons: Array.isArray(input.buttons) ? input.buttons.filter(b => b && b.type && b.text) : [],
          status: "DRAFT", // DRAFT → (criar) PENDING → APPROVED/REJECTED/FAILED
        };
        commitEls(es =>
          es.map(el =>
            el.id === nid
              ? { ...el, data: { ...el.data, templateDraft: draft, templateName: elementName, templateBody: draft.content } }
              : el
          )
        );
        setDirty(true);
        return { ok: true, elementName, status: "DRAFT", note: "Rascunho criado — vira pendência no canvas; o usuário confirma e o WhatsApp aprova (assíncrono)." };
      }
      return { error: `ferramenta desconhecida: ${toolName}` };
    } catch (e) {
      return { error: e.message };
    }
  }, []);

  // gatilho "Acionamento manual": roda o fluxo PUBLICADO agora (botão no nó).
  const onRunManual = async () => {
    const id = appIdRef.current || numericId;
    if (!id) { toast.info("Salve e publique o fluxo antes de executar."); return; }
    try {
      await api.post(`/applications/${id}/run`);
      toast.success("Fluxo executado. Veja a execução no painel.");
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <BuilderActionsContext.Provider
      value={{
        onAddAfter: readOnly ? () => {} : onAddAfter,
        onIterateList: readOnly ? null : (nodeId, listToken) => setPendingIterate({ sourceId: nodeId, listToken }),
        readOnly,
        // nó que está executando agora (destaque pulsante). Vai pelo contexto, e
        // não dentro de data, porque `data` é o que se SALVA — um marcador de
        // execução gravado no fluxo seria lixo permanente.
        nodeRodando,
        agentNames,
        onOpenTemplates: nodeId => { setTemplateFocus(nodeId || null); setTemplateModalOpen(true); },
        onRunManual,
        // card do canvas vazio ("Como quer começar?")
        onPickTrigger: () => setTriggerModalOpen(true),
        // hero ativo: o canvas 30% esconde o card (o hero do chat é a porta de entrada);
        // e enquanto o copiloto monta um fluxo vazio, o nó provisório ocupa o lugar dele
        heroActive: heroMode || copilotBuilding,
        onOpenSetup: guide => setToolGuide(guide || null),
        // sem copiloto habilitado, o card mostra só "Adicionar gatilho"
        onOpenCopilot: COPILOT_ENABLED
          ? () => { setCopilotCollapsedP(false); pulseCopilot(); }
          : null,
      }}
    >
      <AppBar position="static" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton edge="start" className={classes.backBox} onClick={() => tryLeave(backTo)}>
            <BackIcon fontSize="small" />
          </IconButton>
          <div className={classes.idy}>
            <TextField
              className={classes.nameField}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={i18n.t("apps.namePlaceholder")}
              size="small"
              InputProps={{ readOnly }}
            />
            <div className={classes.statusLine}>
              <span className={clsx(classes.statusDot, publishedVersion != null ? classes.dotOk : classes.dotDraft)} />
              {publishedVersion != null ? (
                <>
                  {i18n.t("apps.publishedShort")} <span className={classes.statusVer}>v{publishedVersion}</span>
                </>
              ) : (
                i18n.t("apps.unpublished")
              )}
            </div>
          </div>
          {/* Etapa 4: rótulo "em desenvolvimento" — obra em andamento (fora do limite do plano) */}
          {inDevelopment && (
            <Tooltip title="Em desenvolvimento: não conta no limite do plano. Não recebe chamada sem publicar.">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "3px 9px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#8a5a00",
                  background: "rgba(178,106,0,0.12)",
                  marginLeft: 8,
                }}
              >
                <SettingsIcon style={{ fontSize: 13 }} /> Em desenvolvimento
              </span>
            </Tooltip>
          )}
          {/* chip Rascunho: alterações salvas ainda não publicadas (só quando já há
              uma versão publicada p/ estar "à frente" dela). Some ao publicar (dirty→false). */}
          {dirty && publishedVersion != null && (
            <>
              <Tooltip title="Há alterações salvas que ainda não foram publicadas">
                <button type="button" className={classes.draftChip} onClick={e => setDraftMenuAnchor(e.currentTarget)}>
                  <EditModeIcon style={{ fontSize: 12 }} /> Alterações não publicadas
                </button>
              </Tooltip>
              <Menu
                anchorEl={draftMenuAnchor}
                open={!!draftMenuAnchor}
                onClose={() => setDraftMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ className: classes.ctxPaper }}
                MenuListProps={{ disablePadding: true }}
              >
                <MenuItem className={classes.ctxItem} onClick={() => { setDraftMenuAnchor(null); openVersions(); }}>
                  Ver versões publicadas
                </MenuItem>
                <MenuItem className={classes.ctxItem} onClick={() => { setDraftMenuAnchor(null); discardDraft(); }} style={{ color: "#B12A2A" }}>
                  Descartar rascunho
                </MenuItem>
              </Menu>
            </>
          )}
          {!isNew && (
            <Tooltip title="Notas do fluxo">
              <IconButton
                className={clsx(classes.notesBtn, hasNotes && classes.notesBtnOn)}
                onClick={() => setNotesOpen(true)}
              >
                <NotebookPenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isNew && (
            <Tooltip title="Configurações do fluxo">
              <IconButton
                className={clsx(classes.notesBtn, aiEditable && classes.notesBtnOn)}
                onClick={() => setSettingsOpen(true)}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <div className={classes.grow} />
          {!readOnly && (
            <>
              <Tooltip title="Desfazer (Ctrl+Z)">
                <span>
                  <IconButton size="small" className={classes.histBtn} onClick={undo} disabled={!canUndo}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refazer (Ctrl+Shift+Z)">
                <span>
                  <IconButton size="small" className={classes.histBtn} onClick={redo} disabled={!canRedo} style={{ marginRight: 6 }}>
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          {toolMode && (
            <Button variant="outlined" onClick={() => setTesterOpen(true)} style={{ marginRight: 8 }}>
              Testar
            </Button>
          )}
          {/* toggle de modo (Editar / Visualizar) */}
          {/* INDICADOR DE AUTOSAVE (substitui o toggle Editar/Visualizar + o botão
              Salvar): tudo vai pro rascunho sozinho. Não aparece em read-only. */}
          {!readOnly && (
            <div className={classes.saveInd}>
              {saveStatus === "saving" ? (
                <span className={classes.saveMuted}>Salvando…</span>
              ) : saveStatus === "error" ? (
                <button type="button" className={classes.saveErr} onClick={autoSaveDraft} title="Tentar salvar de novo">
                  Erro ao salvar · tentar
                </button>
              ) : lastSavedAt ? (
                <span className={classes.saveMuted}>Salvo · {relSavedText()}</span>
              ) : null}
            </div>
          )}
          {pendingTpls.length > 0 && (
            <div
              className={classes.tplPendingChip}
              title="Templates de WhatsApp pendentes — não dá para publicar ainda. Clique para abrir."
              onClick={() => setTemplateModalOpen(true)}
            >
              <WarnIcon style={{ fontSize: 15 }} />
              {pendingTpls.length} template{pendingTpls.length > 1 ? "s" : ""} pendente{pendingTpls.length > 1 ? "s" : ""}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PublishIcon />}
              onClick={handlePublish}
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            >
              {i18n.t("apps.publish")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={e => setPubMenuAnchor(e.currentTarget)}
              title="Mais opções"
              style={{ minWidth: 34, padding: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: "1px solid rgba(255,255,255,.22)" }}
            >
              <ArrowDropDownIcon />
            </Button>
          </div>
          <Menu
            anchorEl={pubMenuAnchor}
            open={!!pubMenuAnchor}
            onClose={() => setPubMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ className: classes.ctxPaper }}
            MenuListProps={{ disablePadding: true }}
          >
            <MenuItem
              className={classes.ctxItem}
              disabled={isNew}
              onClick={() => { setPubMenuAnchor(null); openVersions(); }}
            >
              <HistoryIcon fontSize="small" />
              {i18n.t("apps.history")}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <LiveEventsPanel
        open={liveOpen}
        onClose={() => setLiveOpen(false)}
        appId={isNew ? null : numericId}
      />

      <div className={clsx(classes.wrap, buildSeedRef.current && classes.seedFade)} ref={rowRef}>
        {/* Copiloto: COLUNA FIXA à esquerda (sem flutuar/minimizar/fechar). Abaixo de
            1280 dá p/ recolher (botão no header do copiloto) → vira uma trilha fina. */}
        {COPILOT_ENABLED && (copilotCollapsed ? (
          <div className={classes.copilotRail}>
            <IconButton className={classes.copilotRailBtn} onClick={() => setCopilotCollapsedP(false)} title="Abrir copiloto">
              <ChevronRightIcon />
            </IconButton>
            <span className={classes.copilotRailLabel}>Copiloto</span>
          </div>
        ) : (
          <CopilotPanel
            column
            open
            onClose={() => setCopilotCollapsedP(true)}
            runTool={runCopilotTool}
            pulse={copilotPulse}
            hero={heroMode}
            widthOverride={copilotWidthOverride}
            onManual={() => setHeroDismissed(true)}
            profile={toolMode ? undefined : collectorMode ? collectorFlowProfile : handoffProfile}
            initialAgent={copilotStartAgent}
            onBusyChange={setCopilotBusy}
            onStatus={setCopilotStatus}
            // fluxo "do copiloto" (alterar sem perguntar): começou vazio com ele, ou
            // está marcado como editável pela IA; senão ele pede 1x por conversa
            flowGuard={copilotFlowGuard}
            projectId={projectId}
            appId={isNew ? null : numericId}
            flowSummary={elements.some(el => isNode(el) && !isTriggerNode(el)) ? flowSummary() : null}
            flowLoading={!isNew && !numericId}
            seed={buildSeedRef.current}
            demo={demoSeedRef.current}
            onAfterRun={structural => {
              // se o copiloto mexeu na estrutura, reorganiza o canvas (ele empilha
              // tudo no mesmo lugar); senão só reenquadra
              if (structural) organizeLayout();
              else if (rfInstance) setTimeout(() => { try { rfInstance.fitView({ padding: 0.2, maxZoom: INITIAL_ZOOM }); } catch (e) { /* ignore */ } }, 80);
              // auto-salva a cada turno do copiloto (persiste nós, gatilho E o nome
              // final) → "salvar quando terminar". Só quando o app já existe.
              autoSaveDraft();
              // tira o realce "+ criado" dos nós depois de alguns segundos (fade)
              if (structural) {
                setTimeout(() => {
                  setElements(els => els.map(el => (isNode(el) && el.data && el.data._hl ? { ...el, data: { ...el.data, _hl: false } } : el)));
                }, 4000);
              }
            }}
          />
        ))}
        <div className={classes.canvas} ref={wrapperRef} onDrop={onDrop} onDragOver={onDragOver}>
          {/* execuções agora em PILL + popover sobreposto ao canvas (substitui a
              antiga coluna). Foco = execução inspecionada (aba Teste & resultados);
              navegar entre execuções e ver in/out de cada nó é útil também ao editar. */}
          {!isNew && (
            <ExecutionsPill
              appId={numericId}
              selectedRunId={selectedRun}
              onSelect={onSelectRun}
              onExit={() => { setSelectedRun(null); setRunData(null); }}
            />
          )}
          {/* banner de leitura SÓ por motivo EXTERNO, sempre dizendo o motivo (hoje só
              projeto protegido; futuros: papel visualizador, versão antiga, execução em foco) */}
          {readOnly && (
            <div className={classes.viewBadge}>
              <ViewModeIcon style={{ fontSize: 16 }} />
              {lockedProject
                ? "Somente leitura — projeto Sistema Numera (mova para outro projeto para editar)"
                : "Somente leitura"}
            </div>
          )}
          <FlowNotesModal
            open={notesOpen}
            value={notes}
            readOnly={readOnly}
            onClose={() => setNotesOpen(false)}
            onSave={saveNotes}
          />
          <FlowSettingsModal
            open={settingsOpen}
            aiEditable={aiEditable}
            onClose={() => setSettingsOpen(false)}
            onSave={saveFlowSettings}
          />
          <WhatsappTemplatePending
            open={templateModalOpen}
            onClose={() => setTemplateModalOpen(false)}
            focusNodeId={templateFocus}
            elements={elements}
            onPatchNode={(nodeId, patch) => {
              setElements(es => es.map(el => (el.id === nodeId ? { ...el, data: { ...el.data, ...patch } } : el)));
              setDirty(true);
            }}
            isSessionOwned={name => sessionTemplatesRef.current.has(name)}
            onTemplateCreated={name => sessionTemplatesRef.current.add(name)}
          />
          <ReactFlow
            elements={displayElements}
            nodeTypes={nodeTypes}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            onConnect={readOnly ? undefined : onConnect}
            onElementsRemove={readOnly ? undefined : onElementsRemove}
            onNodeDragStart={readOnly ? undefined : onNodeDragStart}
            onNodeDrag={readOnly ? undefined : onNodeDrag}
            onNodeDragStop={readOnly ? undefined : onNodeDragStop}
            onSelectionDragStop={readOnly ? undefined : onSelectionDragStop}
            onLoad={setRfInstance}
            onElementClick={(e, el) => {
              if (!isNode(el) || String(el.id).startsWith("loopframe:")) return;
              setSelectedId(el.id);
              // gatilho ainda não escolhido: o nó agora é o card "Como quer
              // começar?", e a escolha está nas opções dele. Abrir o modal aqui
              // atropelaria o "Criar com IA" (o clique borbulha do card).
            }}
            onPaneClick={() => setSelectedId(null)}
            onPaneContextMenu={readOnly ? undefined : e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, kind: "pane" }); }}
            onNodeContextMenu={readOnly ? undefined : (e, node) => {
              e.preventDefault();
              if (!isNode(node) || String(node.id).startsWith("loopframe:")) return;
              // gatilho → "Substituir gatilho"; demais nós → renomear/apagar
              setCtxMenu({ x: e.clientX, y: e.clientY, kind: isTriggerNode(node) ? "trigger" : "node", nodeId: node.id });
            }}
            onSelectionChange={readOnly ? undefined : onSelectionChange}
            multiSelectionKeyCode={MULTI_SELECT_KEY}
            selectionKeyCode={BOX_SELECT_KEY}
            deleteKeyCode={readOnly ? null : 46}
            snapToGrid
            snapGrid={[12, 12]}
            maxZoom={3}
          >
            <Background variant="lines" gap={20} size={1} color="#ECEAE3" />
            <Controls onFitView={() => { const rf = rfInstanceRef.current; if (rf) try { rf.fitView({ padding: 0.2, maxZoom: INITIAL_ZOOM }); } catch (e) { /* ignore */ } }}>
              {hasGuide && (
                <ControlButton
                  onClick={() => setSetupOpen(true)}
                  title={`Primeiros passos${pending.length ? ` (${pending.length})` : ""}`}
                >
                  <GuideIcon style={{ width: 14, height: 14, color: pending.length ? "#8a6d00" : undefined }} />
                </ControlButton>
              )}
            </Controls>
          </ReactFlow>

          {/* menu de contexto do canvas (clique direito): pane → Organizar; bloco → Renomear/Apagar */}
          <Menu
            open={!!ctxMenu}
            onClose={() => setCtxMenu(null)}
            anchorReference="anchorPosition"
            anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
            PaperProps={{ className: classes.ctxPaper }}
            MenuListProps={{ disablePadding: true }}
          >
            {ctxMenu && ctxMenu.kind === "node" && [
              <MenuItem key="rename" className={classes.ctxItem} onClick={() => renameNodeById(ctxMenu.nodeId)}>
                <RenameIcon fontSize="small" /> Renomear
              </MenuItem>,
              <MenuItem key="del" className={clsx(classes.ctxItem, classes.ctxDanger)} onClick={() => removeNodeById(ctxMenu.nodeId)}>
                <DeleteIcon fontSize="small" /> Apagar
              </MenuItem>,
            ]}
            {ctxMenu && ctxMenu.kind === "trigger" && (
              <MenuItem className={classes.ctxItem} onClick={() => { setCtxMenu(null); setSelectedId(ctxMenu.nodeId); setTriggerModalOpen(true); }}>
                <SwapIcon fontSize="small" /> Substituir gatilho
              </MenuItem>
            )}
            {ctxMenu && ctxMenu.kind === "pane" && (
              <MenuItem className={classes.ctxItem} onClick={() => { setCtxMenu(null); organizeLayout(); }}>
                <OrganizeIcon fontSize="small" /> Organizar blocos
              </MenuItem>
            )}
          </Menu>
        </div>
        {multiSelected && !readOnly ? (
          <div className={classes.multiPanel}>
            <div className={classes.multiCount}>{bulkNodes().length} nós selecionados</div>
            <div className={classes.multiHint}>
              Ações aplicadas a todos os nós selecionados (o gatilho é ignorado).
            </div>
            <div className={classes.multiActions}>
              <Button
                variant="outlined"
                startIcon={bulkAllDisabled ? <CheckCircleIcon /> : <BlockIcon />}
                onClick={bulkToggleDisable}
              >
                {bulkAllDisabled ? "Ativar selecionados" : "Desativar selecionados"}
              </Button>
              <Button
                variant="outlined"
                style={{ color: "#B12A2A", borderColor: "#B12A2A" }}
                startIcon={<DeleteIcon />}
                onClick={bulkDelete}
              >
                Excluir selecionados
              </Button>
            </div>
          </div>
        ) : (
          <ConfigDrawer
            node={selectedNode}
            onChange={updateNodeData}
            onDelete={deleteSelected}
            onSwapNode={() => setSwapPickerOpen(true)}
            onPickTrigger={() => setTriggerModalOpen(true)}
            webhookUrl={webhookUrl}
            published={!!publishedVersion}
            dirty={dirty}
            onPublish={() => (publishRef.current ? publishRef.current() : Promise.resolve({ ok: false }))}
            appId={isNew ? null : numericId}
            onSetSample={setTriggerSample}
            onNodeTested={onNodeTested}
            scope={scope}
            varGroups={varGroups}
            varRunAt={lastRunAt}
            readOnly={readOnly}
            runStepData={runStepData}
            runContext={runContext}
            inspecting={!!selectedRun} /* sem gate de readOnly: o in/out da execução escolhida também aparece durante a edição */
            /* "Executar novamente": precisa saber QUAL execução e QUAL nó */
            selectedRunId={selectedRun}
            selectedStep={selectedStep}
            projectId={projectId}
            onTestTool={toolMode ? () => setTesterOpen(true) : undefined}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>

      {/* naming modal: shown on save/publish while the app is still untitled */}
      {/* DIFF antes de publicar: o que mudou vs a versão no ar */}
      <Dialog open={!!publishDiff} onClose={() => setPublishDiff(null)} maxWidth="xs" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>Publicar alterações?</DialogTitle>
        <DialogContent>
          {publishDiff && (publishDiff.total === 0 ? (
            <div>Nenhuma mudança em relação à versão publicada v{publishDiff.version}.</div>
          ) : (
            <>
              <div style={{ marginBottom: 8, color: "#6B7280", fontSize: 13 }}>Mudanças desde a versão publicada v{publishDiff.version}:</div>
              <ul className={classes.diffList}>
                {publishDiff.added.length > 0 && <li><b style={{ color: "#15784B" }}>+{publishDiff.added.length} adicionado(s)</b>: {publishDiff.added.join(", ")}</li>}
                {publishDiff.removed.length > 0 && <li><b style={{ color: "#A23227" }}>−{publishDiff.removed.length} removido(s)</b>: {publishDiff.removed.join(", ")}</li>}
                {publishDiff.changed.length > 0 && <li><b>~{publishDiff.changed.length} alterado(s)</b>: {publishDiff.changed.join(", ")}</li>}
                {(publishDiff.edgesAdded > 0 || publishDiff.edgesRemoved > 0) && <li>Conexões: +{publishDiff.edgesAdded} / −{publishDiff.edgesRemoved}</li>}
                {publishDiff.triggerChanged && <li>Gatilho alterado</li>}
              </ul>
            </>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDiff(null)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={() => { setPublishDiff(null); doPublish(); }}>Publicar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!nameModal} onClose={() => setNameModal(null)} maxWidth="xs" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>Dar nome à automação</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Nome da automação"
            placeholder="Ex.: Confirmação de agendamento"
            value={name === "Sem Título" ? "" : name}
            onChange={e => { setName(e.target.value); if (nameError) setNameError(""); }}
            onKeyDown={e => { if (e.key === "Enter") confirmName(); }}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            error={!!nameError}
            helperText={nameError || " "}
          />
          <TextField
            select
            label="Pasta (opcional)"
            value={folderId == null ? "" : folderId}
            onChange={e => setFolderId(e.target.value === "" ? null : Number(e.target.value))}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
          >
            <MenuItem value="">Sem pasta</MenuItem>
            {folders.map(f => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleNewFolder}
            disabled={!projectId}
            style={{ textTransform: "none", marginTop: 4 }}
          >
            Nova pasta
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameModal(null)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={confirmName}>
            {nameModal && nameModal.action === "publish" ? "Nomear e publicar" : "Nomear e salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!flowIssues} onClose={() => setFlowIssues(null)} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>
          {flowIssues && flowIssues.length === 1 ? "Resolva 1 problema para publicar" : `Resolva ${(flowIssues || []).length} problemas para publicar`}
        </DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
            Corrija os itens abaixo antes de publicar.
          </div>
          {(flowIssues || []).map((iss, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                padding: "10px 12px",
                marginBottom: 8,
                borderRadius: 8,
                border: "1px solid #F2D6D6",
                background: "#FCF4F4",
              }}
            >
              <span style={{ color: "#C92A2A", fontWeight: 700, lineHeight: "20px" }}>•</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2A", flex: 1, userSelect: "text" }}>{iss.message}</span>
              {iss.nodeId && (
                <Tooltip title="Ir ao nó" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => { goToNode(iss.nodeId); setFlowIssues(null); }}
                    style={{ flexShrink: 0, padding: 4, color: "#5B6470", marginTop: -2 }}
                  >
                    <GoToNodeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setFlowIssues(null)}>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={versionsOpen} onClose={() => setVersionsOpen(false)} maxWidth="md" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>{i18n.t("apps.history")}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("apps.version")}</TableCell>
                <TableCell>{i18n.t("apps.status")}</TableCell>
                <TableCell>{i18n.t("apps.deploy")}</TableCell>
                <TableCell>{i18n.t("apps.when")}</TableCell>
                <TableCell>{i18n.t("apps.notes")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map(v => (
                <TableRow key={v.id}>
                  <TableCell>
                    <strong>v{v.version}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      className={v.status === "published" ? classes.chipPub : classes.chipArch}
                      label={
                        v.status === "published"
                          ? i18n.t("apps.live")
                          : i18n.t("apps.archived")
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div>{v.deployStatus}</div>
                    {v.jambonzSid && <div className={classes.sid}>{v.jambonzSid}</div>}
                  </TableCell>
                  <TableCell>
                    {v.deployedAt ? format(new Date(v.deployedAt), "dd/MM/yyyy HH:mm") : "—"}
                  </TableCell>
                  <TableCell>{v.notes || "—"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => handleRestore(v.version)}>
                      {i18n.t("apps.restore")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {versions.length === 0 && (
            <div className={classes.warn} style={{ color: "#888780" }}>
              {i18n.t("apps.noVersions")}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionsOpen(false)}>{i18n.t("apps.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Guia "como configurar NA FERRAMENTA". Existe porque alguns gatilhos não
          são disparados sozinhos: o Mautic não avisa entrada em campanha/segmento
          — quem avisa é uma ação criada lá dentro. Sem isto o cliente ativa o
          gatilho e fica esperando um evento que nunca vem. */}
      <Dialog
        open={!!toolGuide}
        onClose={() => setToolGuide(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: menu.dialogPaper }}
      >
        <DialogTitle>{(toolGuide && toolGuide.title) || "Como configurar"}</DialogTitle>
        <DialogContent dividers>
          {toolGuide && toolGuide.intro && (
            <Typography style={{ fontSize: 13, color: "#5B6470", marginBottom: 14, lineHeight: 1.5 }}>
              {toolGuide.intro}
            </Typography>
          )}
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, lineHeight: 1.6 }}>
            {((toolGuide && toolGuide.steps) || []).map((s, i) => (
              <li key={i} style={{ marginBottom: 10, whiteSpace: "pre-line" }}>{s}</li>
            ))}
          </ol>
          {webhookUrl && (
            <div style={{ marginTop: 16 }}>
              <Typography style={{ fontSize: 12, fontWeight: 600, color: "#5B6470", marginBottom: 6 }}>
                URL para colar na ferramenta
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F6F7F9", border: "1px solid #E6E9ED", borderRadius: 8, padding: "8px 10px" }}>
                <span style={{ flex: 1, minWidth: 0, fontFamily: "monospace", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {webhookUrl}
                </span>
                <Button size="small" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success(i18n.t("apps.copiedShort")); }}>
                  Copiar
                </Button>
              </div>
            </div>
          )}
          {toolGuide && toolGuide.note && (
            <Typography style={{ fontSize: 12.5, color: "#5B6470", marginTop: 12, lineHeight: 1.5 }}>
              {toolGuide.note}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolGuide(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <TriggerModal
        open={triggerModalOpen}
        onClose={() => setTriggerModalOpen(false)}
        onPick={handlePickTrigger}
      />

      <AddNodeModal
        open={swapPickerOpen}
        title="Trocar este nó por outro"
        onClose={() => setSwapPickerOpen(false)}
        onPick={key => {
          setSwapPickerOpen(false);
          swapSelectedNode(key);
        }}
        hasCall={hasCall}
        toolMode={toolMode}
        collectorMode={collectorMode}
      />

      <AddNodeModal
        open={!!pendingAdd || !!pendingIterate}
        onClose={() => { setPendingAdd(null); setPendingIterate(null); }}
        onPick={pendingIterate ? createIterateNode : createNodeAfter}
        hasCall={hasCall}
        toolMode={toolMode}
        collectorMode={collectorMode}
      />

      <ToolTesterModal
        open={testerOpen}
        onClose={() => setTesterOpen(false)}
        params={toolInputParams}
        ensureSaved={ensureSavedForTest}
        onRan={result => result && result.runId && refreshRunSteps(result.runId)}
      />

      <SetupTour
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        setup={setup}
        steps={tourSteps}
        pending={pending}
        elements={elements}
        onFocus={nodeId => setSelectedId(nodeId)}
        rfInstance={rfInstance}
      />
    </BuilderActionsContext.Provider>
  );
};

// indexa os eventos de UMA execução por passo (step) p/ o picker de variáveis.
// O 1º evento do passo vence, mas os desfechos TARDIOS do mesmo passo — status da
// chamada (call.status) e o resultado da IA (ai_result, ex.: Saídas do agente do
// "Transferir para IA") — se FUNDEM na saída, igual ao inspetor de execução.
const indexRunSteps = events => {
  const map = {};
  (events || []).forEach(e => {
    const step = e.data && e.data.step;
    if (!step) return;
    if ((e.type === "call.status" || e.type === "ai_result") && map[step]) {
      map[step] = { ...map[step], output: { ...(map[step].output || {}), ...((e.data && e.data.output) || {}) } };
      return;
    }
    if (!map[step]) map[step] = e.data;
  });
  return map;
};

const Builder = () => (
  <ReactFlowProvider>
    <BuilderInner />
  </ReactFlowProvider>
);

export default Builder;
